// =====================================================================
// MYSTIC MOON — Bracelet d'Alignement (grille officielle JSON)
// Date de naissance → signe tropical + chemin de vie → clé profil → ligne unique mm-official-profiles.json
// Pas de moteur d’intentions : les pierres et textes viennent exclusivement du JSON.
// =====================================================================

(function () {
  "use strict";

  const DEFAULT_WRIST_SIZE = "16";
  const LIFE_PATH_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33];

  const ZODIAC_LABEL_FR = {
    aries: "Bélier",
    taurus: "Taureau",
    gemini: "Gémeaux",
    cancer: "Cancer",
    leo: "Lion",
    virgo: "Vierge",
    libra: "Balance",
    scorpio: "Scorpion",
    sagittarius: "Sagittaire",
    capricorn: "Capricorne",
    aquarius: "Verseau",
    pisces: "Poissons"
  };

  const STONE_EDITORIAL_ALIAS_NORMALIZED = {
    prehnite: "Préhnite",
    prhenite: "Préhnite",
    prénite: "Préhnite",
    prenite: "Préhnite",
    "azurite malachite": "Azurite-malachite",
    azuritemalachite: "Azurite-malachite",
    labradorite: "Labradorite",
    "pierre de lune blanche": "Pierre de lune",
    "oil de faucon": "Œil de faucon",
    "oeil de faucon": "Œil de faucon",
    "oil de tigre": "Œil de tigre"
  };

  let officialProfilesLoadPromise = null;
  let officialProfilesUrlLoaded = "";

  // -------------------------------------------------------------------
  // Zodiaque & clé profil
  // -------------------------------------------------------------------

  function getWesternZodiacSlug(day, month) {
    const d = parseInt(day, 10) || 1;
    const m = parseInt(month, 10) || 1;
    if ((m === 3 && d >= 21) || (m === 4 && d <= 19)) return "aries";
    if ((m === 4 && d >= 20) || (m === 5 && d <= 20)) return "taurus";
    if ((m === 5 && d >= 21) || (m === 6 && d <= 20)) return "gemini";
    if ((m === 6 && d >= 21) || (m === 7 && d <= 22)) return "cancer";
    if ((m === 7 && d >= 23) || (m === 8 && d <= 22)) return "leo";
    if ((m === 8 && d >= 23) || (m === 9 && d <= 22)) return "virgo";
    if ((m === 9 && d >= 23) || (m === 10 && d <= 22)) return "libra";
    if ((m === 10 && d >= 23) || (m === 11 && d <= 21)) return "scorpio";
    if ((m === 11 && d >= 22) || (m === 12 && d <= 21)) return "sagittarius";
    if ((m === 12 && d >= 22) || (m === 1 && d <= 19)) return "capricorn";
    if ((m === 1 && d >= 20) || (m === 2 && d <= 18)) return "aquarius";
    return "pisces";
  }

  function getWesternZodiacFromBirth(birth) {
    if (!birth) return { slug: "aries", label: ZODIAC_LABEL_FR.aries };
    const slug = getWesternZodiacSlug(birth.day, birth.month);
    return { slug, label: ZODIAC_LABEL_FR[slug] || slug };
  }

  function buildOfficialProfileKey(zodiacSlug, lifePathNumber) {
    return `${zodiacSlug}_${lifePathNumber}`;
  }

  // -------------------------------------------------------------------
  // Numérologie (chemin depuis la date ; expression/âme optionnels via prénom)
  // -------------------------------------------------------------------

  function getLetters(str) {
    return String(str || "").toUpperCase().replace(/[^A-ZÀÂÄÉÈÊËÏÎÔÙÛÜŸÇ]/g, "");
  }

  function getLetterValue(letter) {
    const table = { A:1, J:1, S:1, B:2, K:2, T:2, C:3, L:3, U:3, D:4, M:4, V:4, E:5, N:5, W:5, F:6, O:6, X:6, G:7, P:7, Y:7, H:8, Q:8, Z:8, I:9, R:9 };
    return table[letter] || 0;
  }

  function reduceTo9KeepingMasters(num) {
    if (!Number.isFinite(num) || num <= 0) return num || 0;
    if (num === 11 || num === 22 || num === 33) return num;
    let n = num;
    while (n > 9) {
      n = String(n).split("").reduce((sum, digit) => sum + parseInt(digit, 10), 0);
      if (n === 11 || n === 22 || n === 33) return n;
    }
    return n;
  }

  function displayNumber(num) {
    if (num === 11 || num === 22 || num === 33) return String(num);
    return String(num);
  }

  function sumStringLetters(str) {
    return getLetters(str).split("").reduce((sum, ch) => sum + getLetterValue(ch), 0);
  }

  function sumVowels(str) {
    const vowels = new Set(["A", "E", "I", "O", "U", "Y", "À", "Â", "Ä", "É", "È", "Ê", "Ë", "Ï", "Î", "Ô", "Ù", "Û", "Ü"]);
    return getLetters(str).split("").reduce((sum, ch) => sum + (vowels.has(ch) ? getLetterValue(ch) : 0), 0);
  }

  function parseBirthdate(dateStr) {
    const cleaned = String(dateStr || "").replace(/[^\d]/g, "");
    if (cleaned.length !== 8) return null;
    const day = parseInt(cleaned.slice(0, 2), 10);
    const month = parseInt(cleaned.slice(2, 4), 10);
    const year = parseInt(cleaned.slice(4, 8), 10);
    if (!isValidDateParts(year, month, day)) return null;
    return { day, month, year };
  }

  function isValidDateParts(year, month, day) {
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return false;
    const dt = new Date(year, month - 1, day);
    return dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
  }

  function birthdateToIso(birth) {
    if (!birth) return "";
    const m = String(birth.month).padStart(2, "0");
    const d = String(birth.day).padStart(2, "0");
    return `${birth.year}-${m}-${d}`;
  }

  // --- Profil officiel : numéro de profil + date FR ---
  const ZODIAC_ORDER = ['aries','taurus','gemini','cancer','leo','virgo',
    'libra','scorpio','sagittarius','capricorn','aquarius','pisces'];
  const LIFEPATH_ORDER = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 22, 33];

  function computeProfileNumber(zodiacSlug, lifePath) {
    const zi = ZODIAC_ORDER.indexOf(zodiacSlug);
    const li = LIFEPATH_ORDER.indexOf(Number(lifePath));
    if (zi < 0 || li < 0) return null;
    return String(zi * 12 + li + 1).padStart(3, '0');
  }

  const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet',
    'août','septembre','octobre','novembre','décembre'];

  function formatDateFR(day, month, year) {
    const m = MONTHS_FR[Number(month) - 1];
    if (!day || !m || !year) return '';
    const d = Number(day) === 1 ? '1er' : Number(day);
    return `${d} ${m} ${year}`;
  }

  // Date d'avis "AAAA-MM-JJ" → "mai 2026" (mois + année, registre avis).
  function formatReviewDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    if (!m) return "";
    const month = MONTHS_FR[parseInt(m[2], 10) - 1];
    return month ? `${month} ${m[1]}` : "";
  }

  function formatBirthdateDisplay(birth) {
    if (!birth) return "";
    const d = String(birth.day).padStart(2, "0");
    const m = String(birth.month).padStart(2, "0");
    return `${d} / ${m} / ${birth.year}`;
  }

  function calculateLifePathNumber(dateObj) {
    if (!dateObj) return 1;
    const digits = `${dateObj.day}${dateObj.month}${dateObj.year}`.split("").map(Number);
    return reduceTo9KeepingMasters(digits.reduce((sum, digit) => sum + digit, 0));
  }

  // Décomposition du calcul du chemin de vie pour affichage (section "Votre chemin de vie").
  // Même méthode que calculateLifePathNumber → .final === calculateLifePathNumber(birth).
  function computeLifePathBreakdown(birth) {
    if (!birth) return null;
    const groups = [String(birth.day), String(birth.month), String(birth.year)]
      .map(s => s.split("").map(Number));
    const digits = groups.flat();
    const sum = digits.reduce((a, b) => a + b, 0);
    const steps = [];
    let n = sum;
    while (n > 9 && n !== 11 && n !== 22 && n !== 33) {
      const parts = String(n).split("").map(Number);
      n = parts.reduce((a, b) => a + b, 0);
      steps.push({ parts, result: n });
    }
    return { groups, digits, sum, steps, final: n };
  }

  function buildProfileFromIdentity(firstnames, lastname, birthdateStr) {
    const birth = parseBirthdate(birthdateStr);
    const fullIdentity = `${firstnames} ${lastname}`.trim();
    return {
      lifePath: calculateLifePathNumber(birth),
      expression: reduceTo9KeepingMasters(sumStringLetters(fullIdentity)),
      soul: reduceTo9KeepingMasters(sumVowels(fullIdentity))
    };
  }

  // -------------------------------------------------------------------
  // Normalisation textes / matching Shopify
  // -------------------------------------------------------------------

  function normalizeText(str) {
    return (str || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/œ/gi, "oe")
      .replace(/æ/gi, "ae")
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
  }

  const RETAIL_STONE_PREFIXES = ["perle ", "perles ", "bille ", "billes "];

  const PREHNITE_SPELLING_KEYS = new Set(["prehnite", "prhenite"]);

  function stripRetailStonePrefixes(normalizedKey) {
    let k = (normalizedKey || "").trim();
    if (!k) return "";
    let prev;
    while (k !== prev) {
      prev = k;
      for (let i = 0; i < RETAIL_STONE_PREFIXES.length; i++) {
        const p = RETAIL_STONE_PREFIXES[i];
        if (k.startsWith(p)) {
          k = k.slice(p.length).trim();
          break;
        }
      }
    }
    return k;
  }

  function normInPrehniteFamily(norm) {
    if (!norm) return false;
    if (PREHNITE_SPELLING_KEYS.has(norm)) return true;
    const s = stripRetailStonePrefixes(norm);
    return !!(s && PREHNITE_SPELLING_KEYS.has(s));
  }

  function prehniteFamilyMatch(shopKeyNorm, editorialTargetNorm) {
    return normInPrehniteFamily(shopKeyNorm) && normInPrehniteFamily(editorialTargetNorm);
  }

  function applyEditorialStoneAlias(rawDisplay) {
    const trim = String(rawDisplay || "").trim();
    if (!trim) return trim;
    const k = normalizeText(trim);
    const canon = STONE_EDITORIAL_ALIAS_NORMALIZED[k];
    return canon != null ? canon : trim;
  }

  function collectNormalizedStoneKeysFromBead(b) {
    const raw = [
      b.metaStoneName,
      b.name,
      stripRetailStonePrefixes(normalizeText(b.metaStoneName || b.name))
    ].filter(Boolean);
    const out = new Set();
    raw.forEach((x) => {
      const n = normalizeText(x);
      if (n) out.add(n);
    });
    return [...out];
  }

  function shopKeyMatchesEditorialTarget(shopKey, editorialTargetNorm) {
    if (!shopKey || !editorialTargetNorm) return false;
    if (prehniteFamilyMatch(shopKey, editorialTargetNorm)) return true;
    if (shopKey === editorialTargetNorm) return true;
    const coreShop = stripRetailStonePrefixes(shopKey);
    const coreEd = stripRetailStonePrefixes(editorialTargetNorm);
    if (coreShop && coreEd && coreShop === coreEd) return true;
    return false;
  }

  function beadMatchesEngineStone(b, engineStoneName) {
    const target = normalizeText(applyEditorialStoneAlias(engineStoneName));
    if (!target) return false;
    for (const shopKey of collectNormalizedStoneKeysFromBead(b)) {
      if (shopKeyMatchesEditorialTarget(shopKey, target)) return true;
    }
    return false;
  }

  function availableCatalogMatchesEngineStone(availableNamesNormSet, engineStoneNorm) {
    for (const k of availableNamesNormSet) {
      if (shopKeyMatchesEditorialTarget(k, engineStoneNorm)) return true;
    }
    return false;
  }

  function beadFromSource(el) {
    const intentionsRaw = (el.dataset.intentions || "")
      .split("|")
      .map(s => s.trim())
      .filter(Boolean);

    return {
      name: el.dataset.name || "",
      metaStoneName: el.dataset.metaStoneName || el.dataset.name || "",
      id: parseInt(el.dataset.id, 10),
      price: parseInt(el.dataset.price, 10) || 0,
      imageSrc: el.dataset.img || "",
      metaImageSrc: el.dataset.metaImg || "",
      enBref: el.dataset.enBref || "",
      intentions: intentionsRaw,
      resume: el.dataset.resume || "",
      chakras: (el.dataset.chakras || "")
        .split("|")
        .map(c => c.trim())
        .filter(Boolean)
        .map(entry => {
          const [name, icon] = entry.split("::");
          return { name: (name || "").trim(), icon: (icon || "").trim() };
        }),
      url: el.dataset.url || "",
      sizeMm: parseFloat(el.dataset.sizeMm || "8") || 8
    };
  }

  function findShopifyBead(stoneName, availableBeads) {
    if (!availableBeads?.length) return null;
    const variants = [
      String(stoneName || "").trim(),
      String(stoneName || "").replace(/\s*\([^)]*\)\s*$/, "").trim()
    ].filter(Boolean);
    const seen = new Set();
    for (let i = 0; i < variants.length; i++) {
      const v = variants[i];
      const t = normalizeText(v);
      if (!t || seen.has(t)) continue;
      seen.add(t);
      const hit = availableBeads.find(b => beadMatchesEngineStone(b, v));
      if (hit) return hit;
    }
    const t = normalizeText(variants[0] || "");
    if (t.length < 3) return null;
    const tw = t.split(/\s+/).filter(Boolean);
    return (
      availableBeads.find(b => {
        for (const k of collectNormalizedStoneKeysFromBead(b)) {
          const s = stripRetailStonePrefixes(k);
          if (!s || s.length < 2) continue;
          const words = s.split(/\s+/).filter(Boolean);
          if (tw.length === 1) {
            if (tw[0].length >= 3 && words.includes(tw[0])) return true;
          } else if (tw.every(w => w.length >= 2 && words.includes(w))) return true;
        }
        return false;
      }) || null
    );
  }

  function heroIntentionFromBead(intentions, idx) {
    // Intention retenue (heroIntentionIndex) lue EN LIVE dans la liste metaobject de la pierre.
    // Fallback "" si pas d'index (profil Chantier B en attente) ou index hors borne.
    if (Array.isArray(intentions) && Number.isInteger(idx) && idx >= 0 && idx < intentions.length) {
      return intentions[idx];
    }
    return "";
  }

  function enrichOfficialStonesWithShopify(engineStones, availableBeads) {
    return engineStones.map((s) => {
      const shopify = findShopifyBead(s.nom, availableBeads);
      if (!shopify) {
        console.warn(`[Mystic Moon] Pierre officielle introuvable dans le catalogue Shopify : « ${s.nom} »`);
        return {
          name: s.nom,
          metaStoneName: s.nom,
          id: null,
          price: 0,
          imageSrc: "",
          metaImageSrc: "",
          enBref: "",
          intentions: [],
          intention: "",
          resume: "",
          chakras: [],
          url: "",
          sizeMm: 8,
          intentionRetenue: "",
          intentionRetenueLabel: "Officiel",
          intentionSource: s.source,
          intentionSourceRaison: s.raison,
          rangListeCatalogue: s.rangListe,
          shopifyMissing: true,
          editorialContribution: s.contribution || "",
          editorialContributionShort: s.contributionShort || "",
          imageMetaobjetSrc: s.imageMetaobjet || ""
        };
      }
      return {
        ...shopify,
        intentionRetenue: heroIntentionFromBead(shopify.intentions, s.heroIntentionIndex),
        intentionRetenueLabel: "Officiel",
        intentionSource: s.source,
        intentionSourceRaison: s.raison,
        rangListeCatalogue: s.rangListe,
        editorialContribution: s.contribution || "",
        editorialContributionShort: s.contributionShort || "",
        imageMetaobjetSrc: s.imageMetaobjet || ""
      };
    });
  }

  // -------------------------------------------------------------------
  // Chargement JSON officiel + coercion schéma
  // -------------------------------------------------------------------

  function loadOfficialProfilesFromAsset(url) {
    if (!url) return Promise.resolve({ ok: true, skipped: true });

    if (
      window.MM_OFFICIAL_PROFILES_LOADED
      && officialProfilesUrlLoaded === url
      && window.MM_OFFICIAL_PROFILES
      && Object.keys(window.MM_OFFICIAL_PROFILES).length
    ) {
      return Promise.resolve({ ok: true });
    }

    if (officialProfilesLoadPromise && officialProfilesUrlLoaded === url) {
      return officialProfilesLoadPromise;
    }

    officialProfilesLoadPromise = fetch(url, { credentials: "same-origin" })
      .then(res => {
        if (!res.ok) {
          throw new Error(
            `Impossible de charger les profils (réponse ${res.status}). Vérifiez que le thème publie bien mm-official-profiles.json.`
          );
        }
        return res.json();
      })
      .then(data => {
        const merged = Object.assign(window.MM_OFFICIAL_PROFILES || {}, data || {});
        const n = Object.keys(merged).length;
        if (!n) throw new Error("Le fichier mm-official-profiles.json est vide ou invalide.");
        window.MM_OFFICIAL_PROFILES = merged;
        window.MM_OFFICIAL_PROFILES_LOADED = true;
        officialProfilesUrlLoaded = url;
        return { ok: true };
      })
      .catch(err => {
        console.warn("[Mystic Moon] Impossible de charger mm-official-profiles.json", err);
        officialProfilesLoadPromise = null;
        return { ok: false, err };
      });

    return officialProfilesLoadPromise;
  }

  // Entretien personnalisé : charge mm-profile-care.json (clé = profileKey). Optionnel
  // (dégradation gracieuse : si absent, la section entretien reste masquée).
  let profileCareLoadPromise = null;
  function loadProfileCareFromAsset(url) {
    if (!url) return Promise.resolve({ ok: false });
    if (window.MM_PROFILE_CARE) return Promise.resolve({ ok: true });
    if (profileCareLoadPromise) return profileCareLoadPromise;
    profileCareLoadPromise = fetch(url, { credentials: "same-origin" })
      .then(res => { if (!res.ok) throw new Error(`care ${res.status}`); return res.json(); })
      .then(data => { window.MM_PROFILE_CARE = data || {}; return { ok: true }; })
      .catch(err => {
        console.warn("[Mystic Moon] mm-profile-care.json non chargé", err);
        profileCareLoadPromise = null;
        return { ok: false, err };
      });
    return profileCareLoadPromise;
  }

  // Avis BOUTIQUE (preuve sociale atelier) : charge mm-reviews.json. Optionnel
  // (dégradation gracieuse : si absent ou vide, le bloc avis reste masqué).
  let reviewsLoadPromise = null;
  function loadReviewsFromAsset(url) {
    if (!url) return Promise.resolve({ ok: false });
    if (window.MM_REVIEWS) return Promise.resolve({ ok: true });
    if (reviewsLoadPromise) return reviewsLoadPromise;
    reviewsLoadPromise = fetch(url, { credentials: "same-origin" })
      .then(res => { if (!res.ok) throw new Error(`reviews ${res.status}`); return res.json(); })
      .then(data => { window.MM_REVIEWS = data || {}; return { ok: true }; })
      .catch(err => {
        console.warn("[Mystic Moon] mm-reviews.json non chargé", err);
        reviewsLoadPromise = null;
        return { ok: false, err };
      });
    return reviewsLoadPromise;
  }

  function coerceOfficialProfile(profileKey, raw) {
    if (!raw || typeof raw !== "object") return null;
    const parts = profileKey.split("_");
    const slug = raw.zodiacSlug || parts[0] || "aries";
    const lp = raw.lifePath ?? raw.chemin_de_vie ?? parseInt(parts[parts.length - 1], 10);
    const zodiacLabel = raw.zodiacLabel || raw.signe_label || ZODIAC_LABEL_FR[slug] || slug;

    let stones = [];
    if (Array.isArray(raw.stones) && raw.stones.length && typeof raw.stones[0] === "object" && raw.stones[0] !== null) {
      stones = raw.stones.map(s => ({
        name: String(s.name || "").trim(),
        contribution: String(s.contribution || s.apport || "").trim(),
        contributionShort: String(s.contributionShort || "").trim(),
        imageMetaobjet: String(s.imageMetaobjet || "").trim()
      }));
    } else if (Array.isArray(raw.stones)) {
      const ap = raw.apports_pierres || [];
      raw.stones.forEach((n, i) => {
        stones.push({
          name: String(n || "").trim(),
          contribution: String(ap[i] || "").trim(),
          contributionShort: "",
          imageMetaobjet: ""
        });
      });
    }

    const storyBits = [raw.profil_psychologique, raw.equilibre_recherche].filter(Boolean).join("\n\n");
    const combinedPortrait =
      raw.combinedPortrait
      || raw.story
      || raw.texte_revelation
      || storyBits
      || "";

    return {
      profile: (raw.profile || `${zodiacLabel} ${displayNumber(lp)}`).trim(),
      zodiacSlug: slug,
      zodiacLabel,
      lifePath: lp,
      braceletName: raw.braceletName || raw.heroTitle || raw.titre_creation || "",
      signProfile: raw.signProfile || "",
      numerologyProfile: raw.numerologyProfile || raw.promesse || raw.promesse_equilibre || "",
      combinedPortrait,
      combinedPortraitV2: raw.combinedPortraitV2 || "",
      shadows: raw.shadows || raw.profil_psychologique || "",
      shadowsV2: raw.shadowsV2 || "",
      workThemes: raw.workThemes || raw.accroches || "",
      workThemesV2: raw.workThemesV2 || "",
      stones,
      affirmation: raw.affirmation || "",
      visualPalette: raw.visualPalette || raw.palette_visuelle || "",
      emotionalRendering: raw.emotionalRendering || raw.rendu_emotionnel || "",
      heroImage: raw.heroImage || raw.image_bracelet_studio || null,
      heroIntentionIndex: Array.isArray(raw.heroIntentionIndex) ? raw.heroIntentionIndex : null,
      published: raw.published !== false
    };
  }

  function getOfficialProfile(profileKey) {
    const raw = (typeof window !== "undefined" && window.MM_OFFICIAL_PROFILES)
      ? window.MM_OFFICIAL_PROFILES[profileKey]
      : null;
    return raw ? coerceOfficialProfile(profileKey, raw) : null;
  }

  function resolveComputationFromBirthdate(birthdateStr, firstnames, lastname) {
    const birth = parseBirthdate(birthdateStr);
    if (!birth) throw new Error("Date de naissance invalide.");

    const zodiac = getWesternZodiacFromBirth(birth);
    const lifePath = calculateLifePathNumber(birth);
    const identity = String(firstnames || "").trim();
    const mini = buildProfileFromIdentity(firstnames || "", lastname || "", birthdateStr);
    const expression = identity ? mini.expression : lifePath;
    const soul = identity ? mini.soul : lifePath;

    const profileKey = buildOfficialProfileKey(zodiac.slug, lifePath);
    const official = getOfficialProfile(profileKey);
    if (!official) {
      const zLab = zodiac.label || zodiac.slug;
      throw new Error(
        `Profil officiel introuvable pour ${zLab} · chemin de vie ${displayNumber(lifePath)}. ` +
        `Vérifiez le fichier mm-official-profiles.json (clé attendue : ${profileKey}).`
      );
    }
    if (!official.stones || official.stones.length < 3 || !official.stones.slice(0, 3).every(s => s.name)) {
      throw new Error(`Profil officiel incomplet pour « ${profileKey} » : trois pierres avec un nom sont requises.`);
    }

    const engineStones = official.stones.slice(0, 3).map((s, i) => ({
      nom: applyEditorialStoneAlias(s.name),
      intention: "officiel",
      source: "officiel",
      rangListe: i + 1,
      raison: null,
      heroIntentionIndex: Array.isArray(official.heroIntentionIndex) ? official.heroIntentionIndex[i] : null,
      contribution: s.contribution || "",
      contributionShort: s.contributionShort || "",
      imageMetaobjet: s.imageMetaobjet || ""
    }));

    return {
      profileKey,
      official,
      profile: {
        lifePath,
        expression,
        soul,
        zodiacSlug: zodiac.slug,
        zodiacLabel: zodiac.label,
        profileKey
      },
      engineStones
    };
  }

  // -------------------------------------------------------------------
  // HTML helpers
  // -------------------------------------------------------------------

  function escapeHtml(str) {
    return (str || "")
      .toString()
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Étoile native Be Yours (.rating-star, vars CSS --rating/--rating-max/--rating-decimal
  // de component-rating.css). Renvoie le <span> interne (à placer dans un .rating).
  function ratingStarInner(value, max) {
    const m = max || 5;
    const v = Math.max(0, Math.min(Number(value) || 0, m));
    const whole = Math.floor(v);
    const decimal = Math.round((v - whole) * 100) / 100;
    return `<span aria-hidden="true" class="rating-star" style="--rating:${whole}; --rating-max:${m}; --rating-decimal:${decimal};"></span>`;
  }

  function markdownBold(str) {
    if (!str) return "";
    return escapeHtml(str)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\n\n/g, "</p><p>");
  }

  function nlToParagraphs(str) {
    const clean = (str || "").trim();
    if (!clean) return "";
    return clean
      .split(/\n+/)
      .map(p => p.trim())
      .filter(Boolean)
      .map(p => `<p>${escapeHtml(p)}</p>`)
      .join("");
  }

  // -------------------------------------------------------------------
  // Rationnement V1 — utilitaires éditoriaux
  // -------------------------------------------------------------------

  /**
   * Extrait les N premières phrases complètes d'un texte.
   *
   * Règle éditoriale (stratégie Mystic Moon V1) :
   * - Ne jamais couper sur une phrase commençant par un connecteur
   *   d'opposition ("Mais", "Or", "Cependant"…) : ces phrases ouvrent
   *   les zones de tension — réservées au livret.
   * - Si la N-ième phrase est un connecteur, on recule d'une phrase.
   * - On retourne toujours au moins 1 phrase même si elle est un connecteur
   *   (cas où le portrait n'aurait qu'une seule phrase).
   */
  function extractFirstSentences(text, max) {
    const clean = (text || "").trim();
    if (!clean) return "";

    // Découpe sur les fins de phrase (point suivi d'un espace + majuscule,
    // ou d'un guillemet, ou de fin de chaîne). On recolle le point.
    const raw = clean.split(/(?<=\.)\s+(?=[A-ZÀÂÄÉÈÊËÎÏÔÙÛÜŸ«""])/);
    const sentences = raw
      .map(s => s.trim())
      .filter(Boolean)
      .map((s) => (s.endsWith(".") || s.endsWith("…") || s.endsWith("!") || s.endsWith("?") ? s : s + "."));

    if (!sentences.length) return clean;

    const OPPOSITION = /^(Mais\b|Or[,\s]|Cependant\b|Pourtant\b|Cela dit\b|Néanmoins\b|Toutefois\b)/i;

    const taken = [];
    for (let i = 0; i < sentences.length; i++) {
      if (taken.length >= max) break;
      // Si cette phrase est une opposition et qu'on a déjà au moins 1 phrase, on arrête
      if (taken.length >= 1 && OPPOSITION.test(sentences[i])) break;
      taken.push(sentences[i]);
    }

    // Garantie : au moins 1 phrase retournée
    if (!taken.length && sentences.length) taken.push(sentences[0]);

    return taken.join(" ").trim();
  }

  /**
   * Dérive la ligne de rôle courte d'une pierre pour l'affichage sur le site.
   *
   * Stratégie : champ contributionShort du JSON s'il existe.
   * Fallback : première phrase de la contribution complète, tronquée à
   * 90 caractères sur un mot entier (comportement historique).
   * La contribution complète reste réservée au livret.
   */
  function deriveStoneSiteRole(contributionShort, contribution) {
    const short = String(contributionShort || "").trim();
    if (short) return short;
    const full = String(contribution || "").trim();
    if (!full) return "";
    const firstSentence = full.split(/\.\s/)[0].trim();
    if (!firstSentence) return "";
    if (firstSentence.length <= 90) {
      return firstSentence.endsWith(".") ? firstSentence : firstSentence + ".";
    }
    const cut = firstSentence.slice(0, 90).replace(/\s+\S*$/, "");
    return cut + "…";
  }

  function getPrimaryFirstname(firstnames = "") {
    const t = String(firstnames || "").trim();
    if (!t) return "";
    return t.split(/\s+/)[0];
  }

  function lowerFirstChar(str) {
    const s = String(str || "").trim();
    return s ? s.charAt(0).toLowerCase() + s.slice(1) : "";
  }

  /**
   * Pont pierre↔profil (Lot 2) : intro courte sous le titre des 3 pierres,
   * personnalisée par signe + chemin de vie. Version riche si les 3 pierres
   * portent une intention retenue (heroIntentionIndex) → « intention (Pierre) » ;
   * sinon repli générique. Retour "" si données insuffisantes (intro masquée).
   */
  function buildStonesIntro(official, profile, stones) {
    const sign = (official && official.zodiacLabel) || "";
    const lp = profile && profile.lifePath;
    if (!sign || lp == null) return "";
    const trio = (stones || []).slice(0, 3).filter(s => s && (s.metaStoneName || s.name));
    if (trio.length < 3) return "";
    const allHaveIntent = trio.every(s => (s.intentionRetenue || "").trim());
    // Insécables autour de « · » et avant « : » (typographie FR, évite l'orphelin).
    if (allHaveIntent) {
      const parts = trio.map(s => `${lowerFirstChar(s.intentionRetenue)} (${s.metaStoneName || s.name})`);
      return `Choisies pour votre profil ${sign} · chemin de vie ${lp} : ${parts.join(", ")}.`;
    }
    // Repli sans « Trois pierres » (déjà porté par le titre « Trois pierres, trois appuis »).
    return `Choisies pour votre profil ${sign} · chemin de vie ${lp}, chacune accordée à votre équilibre.`;
  }

  /**
   * Capitalise la 1ère lettre + chaque lettre après un tiret ou une apostrophe.
   * Exemples : "sophie" → "Sophie", "marie-claire" → "Marie-Claire",
   * "d'artagnan" → "D'Artagnan", "élise" → "Élise".
   * Pas d'upper après un espace (évite "Van Gogh" pour des particules).
   */
  function capitalizeFirstname(name) {
    const trimmed = String(name || "").trim();
    if (!trimmed) return "";
    return trimmed.replace(/(^|[\-'])(\p{L})/gu, (_, sep, letter) => sep + letter.toUpperCase());
  }

  function getNameArticle(name = "") {
    const n = String(name || "").trim().toLowerCase();
    if (!n) return "de ";
    if ("aeiouyàáâäéèêëìíîïòóôöùúûü".includes(n[0])) return "d'";
    return "de ";
  }

  function cssEscapeAttr(value) {
    return String(value || "")
      .replace(/\\/g, "\\\\")
      .replace(/"/g, '\\"');
  }

  function getReadingContext(formData = {}, giftData = {}) {
    // V1 — copies stratégie hybride Mystic Moon
    const firstname = getPrimaryFirstname(formData.firstnames || "");

    if (giftData?.isGift && firstname) {
      return {
        isGift: true,
        firstname,
        title: `La lecture de ${firstname}`,
        stonesTitle: `Trois pierres, trois appuis`,
        // 09/10 (Mike) : l'e-mail sert à GARDER la lecture, il ne promet pas plus que l'écran.
        leadTitle: `Gardez sa lecture`,
        leadCta: `Recevoir sa lecture par e-mail`,
        leadConsent: `J’accepte de recevoir sa lecture et des nouvelles de Mystic Moon par e-mail. Désinscription possible à tout moment.`,
        leadText: `Recevez sa lecture par e-mail pour y repenser tranquillement avant de l’offrir : son signe, son chemin de vie, le nom de son Bracelet d’Alignement, son affirmation et ses trois pierres.`
      };
    }

    return {
      isGift: false,
      firstname,
      title: firstname ? `${firstname}, votre lecture` : `Votre lecture`,
      stonesTitle: `Trois pierres, trois appuis`,
      leadTitle: `Gardez votre lecture`,
      leadCta: `Recevoir ma lecture par e-mail`,
      leadConsent: `J’accepte de recevoir ma lecture et des nouvelles de Mystic Moon par e-mail. Désinscription possible à tout moment.`,
      leadText: `Cette lecture ne reste pas à l’écran. Recevez-la par e-mail pour la relire quand vous le souhaitez, ou la montrer à un proche : votre signe, votre chemin de vie, le nom de votre Bracelet d’Alignement, son affirmation et ses trois pierres.`
    };
  }

  function readingSectionArticle(title, bodyText) {
    const body = nlToParagraphs(bodyText);
    if (!body) return "";
    return `
      <article class="image-with-text mm-life__reading-native mm-life__reading-line--main">
        <div class="image-with-text__content left mobile-left mm-life__reading-content">
          <p class="image-with-text__subheading h5 image-with-text__subheading--colored">
            ${escapeHtml(title)}
          </p>
          <div class="image-with-text__text rte typeset2 mm-life__reading-description">
            ${body}
          </div>
        </div>
      </article>
    `;
  }

  function buildOfficialReadingInnerHtml(official) {
    // RATIONNEMENT V1 :
    // Site → 2 phrases du portrait combiné + affirmation + accroche livret.
    // signProfile, numerologyProfile, shadows, workThemes, visualPalette,
    // emotionalRendering → réservés exclusivement au livret imprimé.

    const portraitHtml = official.combinedPortraitV2
      ? `<p>${markdownBold(official.combinedPortraitV2)}</p>`
      : (() => { const s = extractFirstSentences(official.combinedPortrait, 2); return s ? `<p>${escapeHtml(s)}</p>` : ""; })();
    const parts = [];

    if (portraitHtml) {
      parts.push(`
        <article class="image-with-text mm-life__reading-native mm-life__reading-line--main mm-life__reading-line--portrait">
          <div class="image-with-text__content left mobile-left mm-life__reading-content">
            <p class="image-with-text__subheading h5 image-with-text__subheading--colored">
              QUI VOUS ÊTES
            </p>
            <div class="image-with-text__text rte typeset2 mm-life__reading-description">
              ${portraitHtml}
            </div>
          </div>
        </article>
      `);
    }

    if (official.shadowsV2 && official.shadowsV2.trim()) {
      parts.push(`
        <article class="image-with-text mm-life__reading-native mm-life__reading-line--main mm-life__reading-line--shadows">
          <div class="image-with-text__content left mobile-left mm-life__reading-content">
            <p class="image-with-text__subheading h5 image-with-text__subheading--colored">
              CE QUI VOUS FREINE
            </p>
            <div class="image-with-text__text rte typeset2 mm-life__reading-description">
              <p>${markdownBold(official.shadowsV2)}</p>
            </div>
          </div>
        </article>
      `);
    }

    // L'amorce « ce que ce bracelet travaille » (workThemesV2) ne s'affiche plus
    // ici : les axes de travail sont désormais réservés au livret (cf. bloc de
    // clôture ci-dessous). Le champ JSON workThemesV2 reste intact.

    // Affirmation : affichée uniquement dans le hero (label « Votre affirmation » +
    // phrase nue) — on ne la répète plus ici pour éviter le doublon sur la page.

    // Bloc de clôture (générique, identique aux 144) — oriente vers le désir et la
    // commande sans fermer la curiosité. CTA discret = lien doré qui scrolle en
    // douceur vers le bloc commande (#lp-finalize-panel), géré par délégation sur
    // sectionRoot (cf. listener plus bas).
    parts.push(`
      <article class="image-with-text mm-life__reading-native mm-life__reading-line--main mm-life__reading-line--close">
        <div class="image-with-text__content left mobile-left mm-life__reading-content">
          <p class="image-with-text__subheading h5 image-with-text__subheading--colored">
            CE QUI VOUS FAIT AVANCER
          </p>
          <div class="image-with-text__text rte typeset2 mm-life__reading-description">
            <p>Le détail complet de votre lecture (ce qui vous freine, vos leviers, vos dons en appui) vous attend dans votre livret, à l'ouverture du coffret. Une lecture qui n'appartient qu'à vous.</p>
            <p class="mm-life__reading-cta">
              <a href="#lp-commerce" class="mm-life__reading-cta-link">Voir mon coffret ↓</a>
            </p>
          </div>
        </div>
      </article>
    `);

    return `
      <div class="mm-life__reading-list mm-life__reading-list--main">
        ${parts.filter(Boolean).join("")}
      </div>
    `;
  }


  // -------------------------------------------------------------------
  // Motif perles
  // -------------------------------------------------------------------

  function getBeadCountFromSize(size) {
    const map = { 15: 21, 16: 22, 17: 23, 18: 24, 19: 25, 20: 26 };
    return map[parseInt(size, 10)] || map[parseInt(DEFAULT_WRIST_SIZE, 10)];
  }

  function buildHarmoniousPattern(stoneObjects, count) {
    const pattern = (stoneObjects || []).filter(Boolean);
    if (pattern.length < 2) return [];

    const n = pattern.length;
    const keys = pattern.map(s => normalizeText(s.metaStoneName || s.name));

    const base = Math.floor(count / n);
    let rem = count % n;
    const quota = pattern.map(() => base);
    for (let r = 0; r < rem; r++) quota[r % n] += 1;

    if (n === 2 && count % 2 === 0) {
      const [a, b] = pattern;
      return Array.from({ length: count }, (_, i) => (i % 2 === 0 ? a : b));
    }

    const seq = [];
    const lastPos = new Map();
    keys.forEach(k => lastPos.set(k, -1e9));
    const used = pattern.map(() => 0);

    for (let pos = 0; pos < count; pos++) {
      let bestIdx = -1;
      let bestScore = -Infinity;
      for (let j = 0; j < n; j++) {
        if (used[j] >= quota[j]) continue;
        const k = keys[j];
        const gap = pos - lastPos.get(k);
        let score = gap * 1000 + (quota[j] - used[j]) * 12;
        if (seq.length) {
          const prevKey = normalizeText(seq[seq.length - 1].metaStoneName || seq[seq.length - 1].name);
          if (prevKey === k) score -= 1e6;
        }
        if (pos === count - 1 && seq.length) {
          const firstKey = normalizeText(seq[0].metaStoneName || seq[0].name);
          if (k === firstKey) score -= 8e5;
        }
        score -= j * 0.01;
        if (score > bestScore) {
          bestScore = score;
          bestIdx = j;
        }
      }
      if (bestIdx < 0) bestIdx = used.findIndex((u, j) => u < quota[j]);
      const chosen = pattern[bestIdx];
      seq.push(chosen);
      used[bestIdx] += 1;
      lastPos.set(keys[bestIdx], pos);
    }
    return seq;
  }

  // -------------------------------------------------------------------
  // Diagnostics léger (optionnel)
  // -------------------------------------------------------------------

  function diagnoseOfficialVsShopify() {
    const beadSources = [...document.querySelectorAll(".lp-bead-source")];
    const available = new Set();
    beadSources.forEach(el => {
      const k = normalizeText(el.dataset.metaStoneName || el.dataset.name || "");
      if (k) available.add(k);
    });
    const profiles = window.MM_OFFICIAL_PROFILES || {};
    const missing = [];
    Object.keys(profiles).forEach(pk => {
      const o = coerceOfficialProfile(pk, profiles[pk]);
      (o.stones || []).forEach(s => {
        const t = normalizeText(applyEditorialStoneAlias(s.name));
        if (t && !availableCatalogMatchesEngineStone(available, t)) {
          missing.push({ profileKey: pk, pierre: s.name });
        }
      });
    });
    console.group("[MysticMoon] Couverture Shopify ↔ pierres officielles");
    if (missing.length) console.table(missing.slice(0, 40));
    else console.log("OK — chaque pierre officielle trouve une entrée .lp-bead-source (normalisation comprise).");
    console.groupEnd();
    return { missing };
  }

  // -------------------------------------------------------------------
  // Application
  // -------------------------------------------------------------------

  function initMysticMoonLifePath() {
    const app = document.getElementById("life-path-app");
    if (!app || app.dataset.initialized === "true") return;
    app.dataset.initialized = "true";

    const sectionRoot = document.getElementById("bracelet-life-path");
    const form = document.getElementById("life-path-form");
    const slotsWrapper = document.getElementById("lp-slots-wrapper");
    const slotsContainer = document.getElementById("lp-slots-container");
    const stonesIntroEl = document.getElementById("lp-stones-intro");
    const braceletNameEl = document.getElementById("lp-bracelet-name");
    const profileLineEl = document.getElementById("lp-profile-line");
    const readingText = document.getElementById("lp-reading-text");
    const summaryList = document.getElementById("lp-summary-list");
    const addToCartBtn = document.getElementById("lp-add-to-cart-top");
    const stickyPrice = document.getElementById("lp-sticky-price");
    const checkoutHint = document.getElementById("lp-checkout-hint");
    const leadForm = document.getElementById("lp-lead-form");
    const emailResultInput = document.getElementById("lp-email-result");
    const leadSubmitBtn = document.getElementById("lp-lead-submit");
    const leadMessage = document.getElementById("lp-lead-message");
    const careZone = document.getElementById("lp-care-zone");
    const careAvoidRow = document.getElementById("lp-care-avoid-row");
    const careAvoid = document.getElementById("lp-care-avoid");
    const careEntretien = document.getElementById("lp-care-entretien");
    const careRecharge = document.getElementById("lp-care-recharge");
    const profileCareUrl = sectionRoot?.dataset.profileCareUrl || "";
    const reviewsZone = document.getElementById("lp-reviews-zone");
    const reviewsStars = document.getElementById("lp-reviews-stars");
    const reviewsScore = document.getElementById("lp-reviews-score");
    const reviewsList = document.getElementById("lp-reviews-list");
    const reviewsUrl = sectionRoot?.dataset.reviewsUrl || "";

    const stepForm = document.getElementById("lp-step-form");
    const stepLoading = document.getElementById("lp-step-loading");
    const stepResult = document.getElementById("lp-step-result");
    const finalizePanel = document.getElementById("lp-finalize-panel");
    const backToFormBtn = document.getElementById("lp-back-to-form");
    const loadingLines = [...document.querySelectorAll(".mm-life__loading-line")];

    const firstnamesInput = document.getElementById("lp-firstnames");
    const firstnameInputForm = document.getElementById("lp-firstname-form");
    const firstnameLabel = document.getElementById("lp-firstname-label");
    const bdayDay = document.getElementById("lp-bday-day");
    const bdayMonth = document.getElementById("lp-bday-month");
    const bdayYear = document.getElementById("lp-bday-year");
    const giftCards = sectionRoot ? [...sectionRoot.querySelectorAll(".mm-life__gift-card")] : [];
    const giftModeInputs = sectionRoot ? [...sectionRoot.querySelectorAll('input[name="gift_mode"]')] : [];
    const giftFields = document.getElementById("lp-gift-fields");
    const giftFromInput = document.getElementById("lp-gift-from");
    const giftMessageInput = document.getElementById("lp-gift-message");


    let currentBracelet = null;
    let loadingLineTimer = null;
    let resizeTimeout = null;
    let finalizePanelObserver = null;

    // Perle gravée « interperle » : sprite WebP (72 frames, grille 9×8) + sélecteur de
    // couleur argent/or (or activé plus tard). La ROTATION est jouée en CSS pur (animation `steps()`
    // cadencée par le navigateur, cf. .css) → cadence régulière, fluide, AUCUNE boucle
    // JS (plus de requestAnimationFrame qui saccadait sous charge). Le JS ne fait que :
    // poser la perle, choisir la sprite (finition), piloter l'ordre d'apparition des
    // perles. Changer de finition = permuter background-image, sans coupure de l'anim.
    const INTERPERLE_SPRITES = {
      // « Couleur argent » = la perle acier réellement expédiée (le plaqué argent lui
      // est visuellement identique) → on montre la sprite inox, le vrai produit.
      // « Couleur or » = plaqué or, activé plus tard (sprite dédiée).
      argent: sectionRoot?.dataset.interperleInox || sectionRoot?.dataset.interperleArgent || "",
      or: sectionRoot?.dataset.interperleOr || ""
    };
    // Libellés client de la finition — partent dans les properties panier
    // (lisibles en admin/atelier) ; le slug brut part dans _config.finish.
    const FINISH_LABELS = { argent: "Couleur argent", or: "Couleur or" };
    // Côté du conteneur sprite vs diamètre perle écran D :
    // interperle Signature 9 mm / perle 8 mm = 1.125× ; dans la sprite la perle occupe ~86 %
    // de la largeur de sa cellule carrée → côté = (1.125 · D) / 0.86 ≈ 1.31 · D.
    const INTERPERLE_SCALE = 1.125 / 0.86;
    // Révélation : l'interperle apparaît SEULE d'abord (LEAD), puis les perles s'ajoutent
    // une à une (STAGGER) dans le sens horaire À PARTIR d'elle. (Indépendant du spin CSS.)
    const INTERPERLE_REVEAL_LEAD = 700;     // ms : l'interperle seule avant la 1re perle
    const INTERPERLE_REVEAL_STAGGER = 70;   // ms : intervalle entre 2 perles
    const finishSelector = document.getElementById("lp-finish");
    let currentFinish = "argent";
    let interperleEl = null;

    // Interperles en hématite (option client, 28/09) : rondelles Ø 6 mm × 1 mm le long
    // du fil, une entre chaque perle sauf à la jonction de l'interperle Signature. Suite
    // validée par Mike : 2 perles de moins que sans rondelles dans chaque taille
    // (15 cm → 19 … 20 cm → 24), écart de diamètre intérieur +0,7 à +2,3 mm.
    // Couleur = celle de l'interperle Signature (un seul bouton, jamais moitié-moitié).
    const SPACER_THICKNESS_MM = 1;
    const SPACER_DIAMETER_MM = 6;
    const SPACER_BEADS_REMOVED = 2;
    const SPACER_LABELS = { true: "Avec", false: "Sans" };
    const spacerSelector = document.getElementById("lp-spacers");
    const spacerWrap = document.getElementById("lp-spacers-wrap");
    // Défaut « Avec » (Mike, 28/09), lu sur la pastille cochée : un retour arrière
    // du navigateur qui restaure « Sans » reste cohérent avec l'anneau.
    let spacersOn = (spacerSelector?.querySelector('input[name="mm-spacers"]:checked')?.value || "avec") === "avec";
    // Perles de l'anneau AFFICHÉ, dans le sens horaire à partir de l'interperle :
    // c'est ce bracelet-là que la fiche de montage redessine (_config.beads).
    let lastRingBeads = null;

    function getBeadSourceNodes() {
      const root = sectionRoot || document.getElementById("bracelet-life-path");
      const scoped = root ? [...root.querySelectorAll(".lp-bead-source")] : [];
      if (scoped.length) return scoped;
      return [...document.querySelectorAll(".lp-bead-source")];
    }

    function getAllAvailableBeads() {
      return getBeadSourceNodes()
        .map(beadFromSource)
        .filter(b => normalizeText(b.name));
    }

    const variantInputs = sectionRoot ? [...sectionRoot.querySelectorAll('input[name="id"]')] : [];

    function getFormData() {
      const day = bdayDay?.value.trim() || "";
      const month = bdayMonth?.value.trim() || "";
      const year = bdayYear?.value.trim() || "";
      // Compose au format JJ / MM / AAAA — parseBirthdate strip les non-digits
      // donc tout séparateur fonctionne, mais on garde le format affichage cohérent.
      const birthdate = (day || month || year)
        ? `${day} / ${month} / ${year}`
        : "";
      return {
        firstnames: firstnameInputForm?.value.trim() || "",
        lastname: "",
        birthdate
      };
    }

    function getGiftData() {
      const selectedMode = sectionRoot?.querySelector('input[name="gift_mode"]:checked')?.value || "self";
      return {
        mode: selectedMode,
        isGift: selectedMode === "gift",
        from: giftFromInput?.value.trim() || "",
        message: giftMessageInput?.value.trim() || ""
      };
    }

    function toggleGiftFields() {
      const giftData = getGiftData();
      if (giftFields) {
        giftFields.hidden = !giftData.isGift;
        giftFields.classList.toggle("is-open", giftData.isGift);
      }
      updateFirstnameLabel();
    }

    function updateFirstnameLabel() {
      if (!firstnameLabel) return;
      const giftMode = sectionRoot?.querySelector('input[name="gift_mode"]:checked')?.value || "self";
      firstnameLabel.textContent = giftMode === "gift"
        ? "Prénom de la personne qui portera le bracelet"
        : "Votre prénom";
    }

    function updateGiftCardsState() {
      if (!giftCards.length) return;
      giftCards.forEach(card => {
        const radio = card.querySelector('input[type="radio"]');
        card.classList.toggle("is-selected", !!radio?.checked);
      });
    }

    function attachGiftCardsListener() {
      if (!giftCards.length) return;
      giftCards.forEach(card => {
        card.addEventListener("click", () => {
          const radio = card.querySelector('input[type="radio"]');
          if (!radio || radio.checked) return;
          radio.checked = true;
          updateGiftCardsState();
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        });
      });
    }

    function attachDateAutoFocus() {
      if (!bdayDay || !bdayMonth || !bdayYear) return;
      const parts = [bdayDay, bdayMonth, bdayYear];

      // Garder uniquement les digits dans chaque champ
      parts.forEach(input => {
        input.addEventListener("input", () => {
          const cleaned = input.value.replace(/\D/g, "");
          if (cleaned !== input.value) input.value = cleaned;
        });
      });

      // Auto-focus next quand maxlength atteint
      bdayDay.addEventListener("input", () => {
        if (bdayDay.value.length >= 2) bdayMonth.focus();
      });
      bdayMonth.addEventListener("input", () => {
        if (bdayMonth.value.length >= 2) bdayYear.focus();
      });

      // Backspace sur champ vide → focus previous
      bdayMonth.addEventListener("keydown", (e) => {
        if (e.key === "Backspace" && !bdayMonth.value) bdayDay.focus();
      });
      bdayYear.addEventListener("keydown", (e) => {
        if (e.key === "Backspace" && !bdayYear.value) bdayMonth.focus();
      });
    }

    function validateFormData(formData) {
      showInlineMessage("lp-form-error", "");
      if (!formData.firstnames) {
        showInlineMessage("lp-form-error", "Merci d’indiquer votre prénom pour personnaliser votre révélation.");
        return false;
      }
      if (formData.firstnames.length > 50) {
        showInlineMessage("lp-form-error", "Votre prénom est trop long (50 caractères maximum).");
        return false;
      }
      const day = bdayDay?.value.trim() || "";
      const month = bdayMonth?.value.trim() || "";
      const year = bdayYear?.value.trim() || "";
      if (!day || !month || !year || !parseBirthdate(formData.birthdate)) {
        showInlineMessage("lp-form-error", "Date de naissance invalide.");
        return false;
      }
      return true;
    }

    function getSelectedWristSize() {
      const selectedVariant = sectionRoot?.querySelector('input[name="id"]:checked');
      return selectedVariant?.dataset.value || DEFAULT_WRIST_SIZE;
    }

    function setDefaultVariantSize() {
      const defaultInput = sectionRoot?.querySelector(`input[name="id"][data-value="${DEFAULT_WRIST_SIZE}"]`);
      if (defaultInput) defaultInput.checked = true;
    }

    function showInlineMessage(id, message, autoClearMs = 0) {
      const el = id ? document.getElementById(id) : null;
      if (!el) return;
      // role/aria-live posés en dur dans le HTML : on dévoile AVANT d'injecter le
      // texte pour que la région live (déjà exposée) annonce la mutation.
      el.hidden = !message;
      el.textContent = message || "";
      if (autoClearMs > 0 && message) {
        window.clearTimeout(el._mmClearTimer);
        el._mmClearTimer = window.setTimeout(() => {
          el.hidden = true;
          el.textContent = "";
        }, autoClearMs);
      }
    }

    function computeBraceletData(forcedSize) {
      const formData = getFormData();
      if (!validateFormData(formData)) return null;

      const wristSize = forcedSize || getSelectedWristSize() || DEFAULT_WRIST_SIZE;
      const availableBeads = getAllAvailableBeads();

      let resolved;
      try {
        resolved = resolveComputationFromBirthdate(formData.birthdate, formData.firstnames, formData.lastname);
      } catch (err) {
        console.error("[Mystic Moon]", err);
        showInlineMessage("lp-form-error", "Nous n’avons pas pu établir votre profil pour cette date. Vérifiez votre date de naissance, puis réessayez.");
        return null;
      }

      const selectedStones = enrichOfficialStonesWithShopify(resolved.engineStones, availableBeads);
      const count = getBeadCountFromSize(wristSize);
      const sequence = buildHarmoniousPattern(selectedStones, count);

      return {
        formData,
        giftData: getGiftData(),
        profileKey: resolved.profileKey,
        official: resolved.official,
        profile: resolved.profile,
        selectedStones,
        sequence,
        wristSize,
        generatedAt: new Date().toISOString()
      };
    }

    function beadPixelSize(sizeMm, stageSize) {
      const wristCm = parseFloat(currentBracelet?.wristSize || DEFAULT_WRIST_SIZE);
      const baseScale = stageSize * 0.0185;
      const scaleFactor = wristCm / 17;
      return sizeMm * baseScale * scaleFactor;
    }

    function getLayoutMetrics() {
      const stageSize = slotsContainer?.offsetWidth || 320;
      const center = stageSize / 2;
      const wristCm = parseFloat(currentBracelet?.wristSize || DEFAULT_WRIST_SIZE);
      const radiusFactor = wristCm / 17;
      const ringRadius = stageSize * 0.36 * radiusFactor;
      return { stageSize, center, ringRadius };
    }

    function computeBraceletLayout(items) {
      const { center, ringRadius, stageSize } = getLayoutMetrics();
      if (!items.length) return [];
      const diameters = items.map(it => beadPixelSize(it.sizeMm || 8, stageSize));
      const totalArc = diameters.reduce((sum, d) => sum + d, 0);
      const circumference = Math.PI * 2 * ringRadius;
      const correction = circumference / totalArc;
      let acc = 0;
      return items.map((it, idx) => {
        const d = diameters[idx] * correction;
        const arcCenter = acc + d / 2;
        const angle = (arcCenter / circumference) * (Math.PI * 2) - Math.PI / 2;
        const x = center + ringRadius * Math.cos(angle);
        const y = center + ringRadius * Math.sin(angle);
        acc += d;
        return { ...it, x, y, diameter: d, angle };
      });
    }

    // Permute la sprite de la finition active. La rotation (animation CSS `steps()`)
    // continue sans coupure : seul background-image change, pas l'animation.
    function applyInterperleFinish(finish) {
      // Garde sur le DOMAINE des finitions (pas sur la sprite) : le choix client
      // reste enregistré (property panier) même si une sprite manque au visuel.
      currentFinish = FINISH_LABELS[finish] ? finish : "argent";
      // Les rondelles suivent la même finition (CSS [data-finish="or"]).
      if (slotsWrapper) slotsWrapper.dataset.finish = currentFinish;
      const url = INTERPERLE_SPRITES[currentFinish];
      if (interperleEl && url) interperleEl.style.backgroundImage = `url("${url}")`;
    }

    function renderHeroVisual(sequence, animateIn) {
      if (!slotsWrapper || !slotsContainer) return;
      const heroImage = currentBracelet?.official?.heroImage;
      const stage = slotsContainer.closest(".mm-life__bracelet-stage") || slotsContainer;

      if (heroImage) {
        // Photo studio : pas d'anneau JS → pas d'interperle Signature, on masque son sélecteur.
        interperleEl = null;
        if (finishSelector) finishSelector.hidden = true;
        // Photo studio : pas d'anneau à redessiner → option masquée et remise à « Sans ».
        if (spacerWrap) spacerWrap.hidden = true;
        spacersOn = false;
        const sans = spacerSelector?.querySelector('input[value="sans"]');
        if (sans) sans.checked = true;
        lastRingBeads = null;
        slotsWrapper.innerHTML = "";
        slotsWrapper.style.transform = "";   // annule un éventuel pivot d'anneau
        slotsWrapper.style.opacity = "1";
        const img = document.createElement("img");
        img.className = "mm-life__hero-studio-photo";
        img.src = heroImage;
        img.alt = currentBracelet?.official?.braceletName || "Bracelet Mystic Moon";
        img.loading = "eager";
        slotsWrapper.appendChild(img);
        return;
      }

      // Sélecteur visible dans le rendu anneau : au lancement il affiche la seule
      // pastille « Argent » (repère produit) ; il redevient un vrai choix dès que
      // « Or » est décommenté dans le Liquid. (L'attribut `hidden` est de toute façon
      // neutralisé par le `display` du natif .product-form__input.)
      if (finishSelector) finishSelector.hidden = false;
      if (spacerWrap) spacerWrap.hidden = false;
      renderBracelet(spacersOn ? buildSpacedSequence(currentBracelet) : sequence, animateIn);
    }

    // Anneau AVEC rondelles : perles recalculées (2 de moins), une rondelle après
    // chaque perle sauf la dernière → la jonction dernière→première reste nue, c'est
    // la seule que findInterperleJunction peut retenir pour l'interperle.
    function buildSpacedSequence(data) {
      const count = getBeadCountFromSize(data?.wristSize) - SPACER_BEADS_REMOVED;
      const beads = buildHarmoniousPattern(data?.selectedStones, count);
      return beads.flatMap((bead, i) => (i < beads.length - 1
        ? [bead, { spacer: true, sizeMm: SPACER_THICKNESS_MM }]
        : [bead]));
    }

    function renderBracelet(sequence, animateIn) {
      if (!slotsWrapper) return;
      slotsWrapper.innerHTML = "";
      slotsWrapper.style.transform = "";   // reset du pivot d'anneau (recalculé plus bas)
      interperleEl = null;
      lastRingBeads = null;                // jamais l'anneau d'un rendu précédent au panier
      if (!sequence?.length) return;

      const reduceMotion = window.matchMedia
        && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // L'anneau de perles est rendu À L'IDENTIQUE (l'interperle Signature ne participe PAS
      // au layout : elle est superposée plus bas). Perles jointives, positions intactes.
      const layout = computeBraceletLayout(sequence);
      const n = layout.length;
      // Jonction porteuse de l'interperle (la plus proche de 3h) calculée AVANT le
      // rendu : les perles se révèlent dans le sens horaire À PARTIR d'elle.
      const junction = (INTERPERLE_SPRITES[currentFinish] && n >= 2) ? findInterperleJunction(layout) : null;
      const startBead = junction ? (junction.i + 1) % n : 0; // 1re perle = clockwise après la jonction
      lastRingBeads = layout.slice(startBead).concat(layout.slice(0, startBead))
        .filter(it => !it.spacer)
        .map(it => it.metaStoneName || it.name || "");
      // Avec rondelles l'anneau compte ~2× plus d'éléments : pas divisé par 2 pour
      // garder la même durée de révélation.
      const stagger = layout.some(it => it.spacer) ? INTERPERLE_REVEAL_STAGGER / 2 : INTERPERLE_REVEAL_STAGGER;
      const revealDelay = (index) => {
        const order = junction ? (index - startBead + n) % n : index;
        const lead = junction ? INTERPERLE_REVEAL_LEAD : 0;
        return `${lead + order * stagger}ms`;
      };
      const animate = animateIn && !reduceMotion;

      layout.forEach((item, index) => {
        if (item.spacer) {
          slotsWrapper.appendChild(buildSpacerSlot(item, index, animate ? revealDelay(index) : null));
          return;
        }
        const slot = document.createElement("div");
        slot.className = "mm-life__bracelet-slot";
        slot.dataset.index = index;

        const stoneName = item.metaStoneName || item.name || "Perle";
        slot.title = stoneName;

        slot.style.width = `${item.diameter}px`;
        slot.style.height = `${item.diameter}px`;
        slot.style.left = `${item.x}px`;
        slot.style.top = `${item.y}px`;
        slot.style.transform = "translate(-50%, -50%)";
        slot.style.zIndex = String(index);

        const img = document.createElement("img");
        const src = (item.imageSrc || item.metaImageSrc || "").trim();
        if (src) img.src = src;
        img.alt = stoneName;
        // Entrée (Lot 2) : perles révélées une à une. Posée sur l'IMG (le slot garde
        // son translate de centrage). Ordre = sens horaire depuis l'interperle, après
        // son avance (LEAD). Désactivée si reduce-motion.
        if (animate) {
          img.classList.add("mm-life__bead--in");
          img.style.animationDelay = revealDelay(index);
        }
        slot.appendChild(img);
        slotsWrapper.appendChild(slot);
      });

      renderInterperleOverlay(layout, junction, animateIn, reduceMotion);
    }

    // Rondelle vue de profil dans l'anneau posé à plat : barrette de 6 mm dans l'axe
    // du rayon × 1 mm le long du fil. item.diameter = son épaisseur à l'écran (arc
    // de 1 mm) → sa longueur en découle. rotate(angle) aligne la longueur sur le rayon.
    function buildSpacerSlot(item, index, delay) {
      const slot = document.createElement("div");
      slot.className = "mm-life__bracelet-slot mm-life__bracelet-slot--spacer";
      slot.dataset.index = index;
      slot.title = "Interperle en hématite";
      slot.style.width = `${item.diameter * (SPACER_DIAMETER_MM / SPACER_THICKNESS_MM)}px`;
      slot.style.height = `${item.diameter}px`;
      slot.style.left = `${item.x}px`;
      slot.style.top = `${item.y}px`;
      slot.style.transform = `translate(-50%, -50%) rotate(${item.angle}rad)`;
      slot.style.zIndex = String(index);

      const disc = document.createElement("div");
      // ignore-empty : div sans enfant, sinon masquée par Be Yours (div:empty, base.css:626).
      disc.className = "mm-life__spacer ignore-empty";
      if (delay) {
        disc.classList.add("mm-life__bead--in");
        disc.style.animationDelay = delay;
      }
      slot.appendChild(disc);
      return slot;
    }

    // Jonction (paire de perles adjacentes) dont le milieu est le plus proche de 3h.
    function findInterperleJunction(layout) {
      const { center } = getLayoutMetrics();
      let best = null;
      for (let i = 0; i < layout.length; i++) {
        const a = layout[i];
        const b = layout[(i + 1) % layout.length];
        if (a.spacer || b.spacer) continue; // jonction occupée par une rondelle
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        const ang = Math.atan2(my - center, mx - center); // angle de la jonction
        const dist = Math.abs(ang);                        // écart à 3h (angle 0)
        if (!best || dist < best.dist) {
          best = { i, mx, my, ang, dist, d: (a.diameter + b.diameter) / 2 };
        }
      }
      return best;
    }

    // Perle gravée = OVERLAY par-dessus l'anneau. Réalité physique : creuse, enfilée
    // PAR-DESSUS la jonction de deux perles qui restent JOINTIVES → elle ne décale
    // aucune perle (ne participe pas à computeBraceletLayout). On la pose à cheval sur
    // la jonction (milieu du segment des 2 centres) la plus proche de 3h, en z-index
    // au-dessus des perles. L'anneau pivote légèrement pour amener cette jonction PILE
    // à 3h (perles rondes → invisible) ; la perle est contre-pivotée pour rester à
    // l'horizontale (axe long = radial = horizontal à 3h).
    function renderInterperleOverlay(layout, junction, animateIn, reduceMotion) {
      if (!INTERPERLE_SPRITES[currentFinish] || !junction) return;

      // Pivot de l'anneau : amène la jonction choisie pile à 3h (angle 0).
      slotsWrapper.style.transform = `rotate(${-junction.ang}rad)`;

      const size = junction.d * INTERPERLE_SCALE;
      const slot = document.createElement("div");
      slot.className = "mm-life__bracelet-slot mm-life__bracelet-slot--interperle";
      slot.title = "Perle gravée Mystic Moon";
      slot.style.width = `${size}px`;
      slot.style.height = `${size}px`;
      slot.style.left = `${junction.mx}px`;
      slot.style.top = `${junction.my}px`;
      // contre-rotation (+ang) = annule le pivot du wrapper → perle horizontale.
      slot.style.transform = `translate(-50%, -50%) rotate(${junction.ang}rad)`;
      slot.style.zIndex = "999"; // au-dessus de toutes les perles

      const bead = document.createElement("div");
      // ignore-empty : div SANS enfant (texture en background) → sinon Be Yours le
      // masque (div:empty, base.css:626 — piège du Lot 2). La rotation est portée par
      // la classe .mm-life__interperle (animation CSS steps, cf. .css).
      bead.className = "mm-life__interperle ignore-empty";
      if (animateIn && !reduceMotion) bead.classList.add("mm-life__bead--in"); // entrée scale
      interperleEl = bead;
      applyInterperleFinish(currentFinish);
      slot.appendChild(bead);
      slotsWrapper.appendChild(slot);
    }

    function renderSummary(selectedStones) {
      if (!summaryList) return;
      summaryList.innerHTML = "";

      if (!selectedStones?.length) {
        const empty = document.createElement("li");
        empty.className = "multicolumn-list__item flex-grid__item";
        empty.innerHTML = `<div class="mm-life__empty">Aucune pierre révélée pour le moment.</div>`;
        summaryList.appendChild(empty);
        return;
      }

      const arrowIcon = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-arrow" fill="none" viewBox="0 0 14 10"><path fill-rule="evenodd" clip-rule="evenodd" d="M8.537.808a.5.5 0 01.817-.162l4 4a.5.5 0 010 .708l-4 4a.5.5 0 11-.708-.708L11.793 5.5H1a.5.5 0 010-1h10.793L8.646 1.354a.5.5 0 01-.109-.546z" fill="currentColor"/></svg>`;

      selectedStones.forEach((item) => {
        const image = item.imageMetaobjetSrc || item.metaImageSrc || item.imageSrc || "";
        const name = escapeHtml(item.metaStoneName || item.name);
        const url = item.url || "";
        const roleText = escapeHtml(deriveStoneSiteRole(item.editorialContributionShort || "", item.editorialContribution || ""));
        const miss = item.shopifyMissing ? " <span class=\"mm-life__stone-missing\">(visuel à venir)</span>" : "";

        // Surtitre : l'UNIQUE intention retenue pour ce profil (heroIntentionIndex), lue en live.
        // Même pattern natif que le surtitre du hero (bleu --color-heading via subheading--colored).
        const retenue = (item.intentionRetenue || "").trim();
        const captionBlock = retenue
          ? `<p class="image-with-text__subheading image-with-text__subheading--colored h5 mm-life__stone-caption">${escapeHtml(retenue)}</p>`
          : "";

        // Wrapper image : structure native multicolumn Be Yours
        // (use-animate « zoom-fade » + anchor superposé full-unstyled-link).
        const imageBlock = image ? `
          <use-animate data-animate="zoom-fade" class="media-wrapper multicolumn-card__image-wrapper multicolumn-card__image-wrapper--full-width">
            <div class="image-animate media media--square">
              <img src="${image}" alt="${name}" loading="lazy" class="multicolumn-card__image">
            </div>
          </use-animate>
        ` : "";

        const li = document.createElement("li");
        li.className = "multicolumn-list__item flex-grid__item left";
        li.innerHTML = `
          <div class="multicolumn-card">
            ${imageBlock}
            <div class="multicolumn-card__info">
              <div class="multicolumn-card__title-wrapper">
                ${captionBlock}
                <p class="multicolumn-card__title h1">${name}${miss}</p>
              </div>
              ${roleText ? `<div class="rte typeset2"><p>${roleText}</p></div>` : ""}
            </div>
          </div>
        `;
        summaryList.appendChild(li);
      });
    }

    function renderReading(official, profile) {
      const context = getReadingContext(
        currentBracelet?.formData || getFormData(),
        currentBracelet?.giftData || getGiftData()
      );

      const profileNumberEl = document.getElementById("lp-profile-number");
      const affirmationEl = document.getElementById("lp-affirmation");
      const readingTitle = document.getElementById("lp-reading-title");
      const stonesTitle = document.getElementById("lp-stones-title");
      const leadTitle = document.getElementById("lp-lead-title");
      const leadText = document.getElementById("lp-lead-text");

      // Ligne profil fusionnée : « Pour Michaël · Bélier, chemin de vie 5 »
      // (sans prénom : « Bélier, chemin de vie 5 », pas de séparateur orphelin).
      if (profileLineEl) {
        const who = context.firstname ? `Pour ${context.firstname} · ` : "";
        profileLineEl.textContent = `${who}${official.zodiacLabel}, chemin de vie ${profile.lifePath}`;
      }
      if (affirmationEl) affirmationEl.textContent = official.affirmation || "";

      if (profileNumberEl) {
        const num = computeProfileNumber(profile.zodiacSlug, profile.lifePath);
        profileNumberEl.textContent = num ? `Composition N° ${String(num).padStart(3, "0")} / 144` : "";
      }

      if (braceletNameEl) braceletNameEl.textContent = official.braceletName || "Bracelet d’Alignement";
      if (readingTitle) readingTitle.textContent = context.title;
      if (stonesTitle) stonesTitle.textContent = context.stonesTitle;

      // Pont pierre↔profil (Lot 2) : intro personnalisée sous le titre des 3 pierres.
      if (stonesIntroEl) {
        const intro = buildStonesIntro(official, profile, currentBracelet?.selectedStones || []);
        stonesIntroEl.textContent = intro;
        stonesIntroEl.hidden = !intro;
      }
      if (leadTitle) leadTitle.textContent = context.leadTitle;
      if (leadText) leadText.innerHTML = `<p>${escapeHtml(context.leadText)}</p>`;
      // Mode cadeau (09/10) : raccourci, bouton et case suivent le titre (« sa » lecture).
      const leadJump = document.getElementById("lp-lead-jump");
      const leadConsentText = document.getElementById("lp-lead-consent-text");
      const leadSubmit = document.getElementById("lp-lead-submit");
      if (leadJump) leadJump.textContent = context.leadCta;
      if (leadSubmit && !leadSubmit.disabled) leadSubmit.textContent = context.leadCta;
      if (leadConsentText) leadConsentText.textContent = context.leadConsent;

      if (readingText) readingText.innerHTML = buildOfficialReadingInnerHtml(official);

      // Entretien personnalisé (fusion des 3 pierres) — lu depuis mm-profile-care.json
      // par clé de profil. Section masquée si donnée absente (dégradation gracieuse).
      if (careZone) {
        const pk = profile.profileKey || buildOfficialProfileKey(profile.zodiacSlug, profile.lifePath);
        const applyCare = () => {
          const c = (window.MM_PROFILE_CARE || {})[pk];
          if (!c) { careZone.hidden = true; return; }
          if (careAvoid) careAvoid.textContent = c.avoid_fr || "";
          if (careAvoidRow) careAvoidRow.hidden = !c.avoid_fr;
          if (careEntretien) careEntretien.textContent = c.entretien_fr || "";
          if (careRecharge) careRecharge.textContent = c.recharge_fr || "";
          careZone.hidden = false;
        };
        if (window.MM_PROFILE_CARE) applyCare();
        else { careZone.hidden = true; loadProfileCareFromAsset(profileCareUrl).then(applyCare); }
      }
    }

    // Bloc avis ATELIER (preuve sociale marque, identique pour tous les profils) :
    // agrégat natif (.rating) + 3 verbatims vedettes. Masqué tant qu'aucun avis.
    function renderReviews() {
      if (!reviewsZone) return;
      const data = window.MM_REVIEWS;
      if (!data) { loadReviewsFromAsset(reviewsUrl).then(() => renderReviews()); return; }

      const list = Array.isArray(data.reviews) ? data.reviews.filter(r => r && r.name) : [];
      const count = Number(data.count) || list.length;
      if (!list.length || !count) { reviewsZone.hidden = true; return; }

      const avg = Number(data.average)
        || (list.reduce((s, r) => s + (Number(r.rating) || 0), 0) / list.length);
      const avgFr = avg.toFixed(1).replace(".", ",");

      if (reviewsStars) {
        reviewsStars.innerHTML = ratingStarInner(avg, 5);
        reviewsStars.setAttribute("aria-label", `${avgFr} sur 5`);
      }
      if (reviewsScore) {
        reviewsScore.textContent = `${avgFr} / 5 · ${count} avis en boutique`;
      }

      // Verbatims vedettes : ceux avec un texte substantiel (≥ 12 car.), max 3.
      // L'agrégat compte bien les ${count} avis ; le JSON est déjà trié.
      if (reviewsList) {
        const featured = list.filter(r => (r.body || "").trim().length >= 12).slice(0, 3);
        reviewsList.innerHTML = featured.map(r => {
          const rate = Number(r.rating) || 0;
          const stars = `<div class="rating" role="img" aria-label="${escapeHtml(rate + " sur 5")}">${ratingStarInner(rate, 5)}</div>`;
          const body = escapeHtml((r.body || "").trim()).replace(/\n+/g, "<br>");
          const date = formatReviewDate(r.date);
          const author = escapeHtml(r.name) + (date ? ` · <span class="mm-life__review-date">${escapeHtml(date)}</span>` : "")
            + (r.verified ? ` · <span class="mm-life__review-verified">Achat vérifié</span>` : "");
          return `<li class="mm-life__review multicolumn-list__item flex-grid__item">
            ${stars}
            <blockquote class="mm-life__review-body typeset2">${body}</blockquote>
            <p class="mm-life__review-author caption">${author}</p>
          </li>`;
        }).join("");
      }

      reviewsZone.hidden = false;
    }

    function renderResult(braceletData, opts) {
      const animateIn = !!(opts && opts.animateIn);
      currentBracelet = braceletData;
      const official = currentBracelet.official;
      const stones = currentBracelet?.selectedStones || [];

      if (checkoutHint) {
        // ⚠️ Ce texte existe AUSSI en Liquid, et il est réécrit ici à chaque
        // révélation : toute variante (borne comprise) doit être déclinée aux
        // DEUX endroits, sinon la version Liquid est écrasée sans rien signaler.
        // En borne on ne renvoie pas vers « écrivez-nous » : le vendeur est là.
        const borneMode = sectionRoot?.dataset.borne === "1";
        checkoutHint.textContent =
          borneMode
            ? (stones.some(s => s.shopifyMissing)
                ? "Le visuel de certaines perles arrive bientôt. Votre composition reste exactement celle révélée ci-dessus. Une question ? Nous sommes à deux pas."
                : "Vérifiez votre taille avant d’envoyer votre composition. Rien n’est payé ici : vous réglez au comptoir.")
            : stones.some(s => s.shopifyMissing)
            ? "Le visuel de certaines perles arrive bientôt ; votre composition reste exactement celle révélée ci-dessus. Un doute ? Écrivez-nous."
            : "Vous commandez la composition révélée ci-dessus. Vous pouvez encore modifier la taille tant que vous n’avez pas confirmé votre commande.";
      }

      // Rendu différé dans tous les cas : renderResult est appelé AVANT
      // showResultStep(), donc l'étape résultat est encore display:none au moment
      // de l'appel → slotsContainer.offsetWidth = 0 et la géométrie des perles
      // retomberait sur le fallback 320px (anneau riquiqui décalé). Le court délai
      // laisse showResultStep() rendre l'étape visible → offsetWidth réel.
      // animateIn=true : perles révélées une à une (stagger) ; sinon simple fondu.
      if (slotsWrapper) slotsWrapper.style.opacity = 0;
      // Visibilité du choix des rondelles fixée TOUT DE SUITE (le rendu est différé) :
      // sinon, sur un profil en photo studio, les pastilles flashent 60-120 ms.
      if (spacerWrap) spacerWrap.hidden = !!currentBracelet?.official?.heroImage;
      lastRingBeads = null; // un ajout au panier avant le rendu différé prend le repli
      setTimeout(() => {
        renderHeroVisual(currentBracelet.sequence, animateIn);
        if (slotsWrapper) slotsWrapper.style.opacity = 1;
      }, animateIn ? 60 : 120);

      renderSummary(currentBracelet.selectedStones);
      renderReading(official, currentBracelet.profile);

      if (firstnamesInput) {
        firstnamesInput.value = currentBracelet.formData?.firstnames || "";
      }
    }

    function clearLoadingTimers() {
      if (loadingLineTimer) clearInterval(loadingLineTimer);
    }

    function teardownFinalizeObserver() {
      if (finalizePanelObserver) {
        finalizePanelObserver.disconnect();
        finalizePanelObserver = null;
      }
      sectionRoot?.removeAttribute("data-checkout-in-view");
    }

    function setupFinalizeObserver() {
      teardownFinalizeObserver();
      if (!sectionRoot || !finalizePanel || typeof IntersectionObserver === "undefined") return;
      // Le formulaire e-mail, remonté au-dessus de la commande (17/10), masque aussi la
      // barre d'achat mobile : sinon elle recouvre le formulaire pendant la saisie.
      const leadZone = document.getElementById("lp-lead-zone");
      const enVue = new Map();
      finalizePanelObserver = new IntersectionObserver(
        entries => {
          entries.forEach(e => enVue.set(e.target, e.isIntersecting && e.intersectionRatio >= 0.12));
          if (!sectionRoot) return;
          if ([...enVue.values()].some(Boolean)) sectionRoot.setAttribute("data-checkout-in-view", "true");
          else sectionRoot.removeAttribute("data-checkout-in-view");
        },
        { root: null, threshold: [0, 0.06, 0.12, 0.2] }
      );
      finalizePanelObserver.observe(finalizePanel);
      if (leadZone) finalizePanelObserver.observe(leadZone);
    }

    // Pose data-flow sur la racine (form/loading/result) — pilote le CSS
    // (sticky mobile, paddings). L'ancien indicateur d'étapes visuel
    // ([data-step-track]) n'a jamais eu de markup → boucle retirée (Lot 3).
    function setStepTracker(phase) {
      if (!sectionRoot) return;
      sectionRoot.setAttribute("data-flow", phase);
      // Dé-doublonnage par état (audit v2) : les sections page-level de l'ENTRÉE
      // (réassurance, « pourquoi », avis, FAQ) sont des .shopify-section SŒURS,
      // hors de portée de data-flow. On pose une classe sur <body> à l'état
      // résultat ; le CSS s'en sert pour les masquer (display:none) afin qu'elles
      // ne fassent pas doublon avec les blocs internes du configurateur.
      document.body.classList.toggle("mm-flow--result", phase === "result");
    }

    function showLoadingStep() {
      const firstname = capitalizeFirstname(firstnameInputForm?.value || "");
      const isGift = sectionRoot?.querySelector('input[name="gift_mode"]:checked')?.value === "gift";
      const loadingTitleEl = document.getElementById("lp-loading-title");
      if (loadingTitleEl) {
        if (!firstname) {
          loadingTitleEl.textContent = "Nous composons votre création…";
        } else if (isGift) {
          const article = getNameArticle(firstname);
          loadingTitleEl.textContent = `Nous composons la création ${article}${firstname}…`;
        } else {
          loadingTitleEl.textContent = `${firstname}, nous composons votre création…`;
        }
      }
      teardownFinalizeObserver();
      setStepTracker("loading");
      stepForm?.classList.remove("mm-life__step--active");
      stepForm?.classList.add("mm-life__step--hidden");
      stepResult?.classList.remove("mm-life__step--active");
      stepResult?.classList.add("mm-life__step--hidden");
      stepLoading?.classList.remove("mm-life__step--hidden");
      stepLoading?.classList.add("mm-life__step--active");

      let activeIndex = 0;
      loadingLines.forEach((line, i) => line.classList.toggle("is-active", i === activeIndex));
      clearLoadingTimers();
      loadingLineTimer = setInterval(() => {
        activeIndex = (activeIndex + 1) % Math.max(1, loadingLines.length);
        loadingLines.forEach((line, i) => line.classList.toggle("is-active", i === activeIndex));
      }, 520);
      scrollToSectionTop();
    }

    // Copie le montant du prix RENDU (server-side, #price-bracelet-life-path)
    // dans la barre sticky mobile — jamais de prix en dur. Prend le prix en
    // promo si affiché, sinon le prix régulier.
    function syncStickyPrice() {
      if (!stickyPrice) return;
      const priceEl = document.getElementById("price-bracelet-life-path");
      const priceRoot = priceEl ? priceEl.querySelector(".price") : null;
      const onSale = priceRoot ? priceRoot.classList.contains("price--on-sale") : false;
      let node = priceEl
        ? priceEl.querySelector(onSale ? ".price-item--sale" : ".price-item--regular")
        : null;
      if (!node && priceEl) node = priceEl.querySelector(".price-item--regular");
      const txt = (node?.textContent || "").replace(/\s+/g, " ").trim();
      if (txt) {
        stickyPrice.textContent = txt;
        stickyPrice.hidden = false;
      } else {
        stickyPrice.hidden = true;
      }
    }

    /**
     * Événement « révélation affichée » — le seul point du tunnel qui n'était
     * mesuré NULLE PART (audit analytics du 06/09/2026).
     *
     * Pourquoi ici : `showResultStep()` est le passage obligé des deux chemins
     * qui montrent un profil (soumission normale et réhydratation après le lead
     * form). Le jeu `sessions` de ShopifyQL n'expose que la page d'ATTERRISSAGE,
     * et la révélation ne change pas d'URL : sans cet événement, « combien de
     * gens lancent une révélation » reste sans réponse, y compris pour Mike
     * dans son admin.
     *
     * Garde-fous :
     *  - AUCUNE donnée personnelle (ni prénom, ni date de naissance) ;
     *  - une seule émission par révélation (une relecture ne recompte pas) ;
     *  - silencieux si l'API n'est pas là (borne, aperçu, consentement refusé) :
     *    la révélation ne doit JAMAIS casser parce que la mesure manque.
     */
    let revelationTracked = "";
    // Relais Cloudflare (borne-worker/worker.js), route du compteur anonyme.
    const WORKER_URL = "https://mystic-moon-borne.mysticmoon.workers.dev";
    const MESURE_URL = WORKER_URL + "/evenement";

    /* BORNE ITINÉRANTE (07/10, docs/cahier-des-charges-borne-itinerante.md).
       Une seule borne, posée n'importe où. Le LIEU et le MODE (avec / sans
       vendeur) sont réglés sur la borne (écran caché du kiosque, stockage local)
       et lus ici. ⚠️ SANS réglage, la borne se comporte exactement comme avant
       (boutique) : tout ce qui suit ne s'active que si un lieu est réglé.
       Côté site, le lieu d'origine d'un visiteur (QR de la borne ou carte du
       lieu) est gardé le temps de la visite seulement (sessionStorage). */
    const REGLAGE_BORNE_KEY = "mm-borne-reglage";
    const LIEU_SESSION_KEY = "mm-lieu-origine";
    const FILE_MESURE_KEY = "mm-borne-file";
    const lieuOk = (l) => {
      const v = String(l || "").trim().toLowerCase();
      return /^[a-z0-9-]{1,40}$/.test(v) ? v : "";
    };
    const reglageBorne = (() => {
      if (sectionRoot?.dataset.borne !== "1") return null;
      try {
        const r = JSON.parse(localStorage.getItem(REGLAGE_BORNE_KEY) || "null");
        const lieu = lieuOk(r?.lieu);
        return lieu ? { lieu, nom: String(r.nom || lieu), mode: r.mode === "sans" ? "sans" : "avec" } : null;
      } catch (e) { return null; }
    })();
    const lieuVisite = () => {
      try { return lieuOk(sessionStorage.getItem(LIEU_SESSION_KEY)); } catch (e) { return ""; }
    };
    const lieuCourant = () => (reglageBorne ? reglageBorne.lieu : lieuVisite());

    // Envoi d'un événement anonyme. Sur la borne, un envoi impossible (réseau
    // coupé) est mis en file et renvoyé au retour de la connexion.
    function envoieMesure(corps) {
      let parti = false;
      try {
        if (navigator.onLine !== false) {
          parti = !!navigator.sendBeacon?.(MESURE_URL, new Blob([JSON.stringify(corps)], { type: "text/plain" }));
        }
      } catch (e) { /* la mesure ne casse jamais le parcours */ }
      if (parti || !reglageBorne) return;
      try {
        const f = JSON.parse(localStorage.getItem(FILE_MESURE_KEY) || "[]");
        f.push(corps);
        localStorage.setItem(FILE_MESURE_KEY, JSON.stringify(f.slice(-500)));
      } catch (e) { /* stockage indisponible : l'événement est perdu, pas le parcours */ }
    }
    function videFileMesure() {
      let f;
      try { f = JSON.parse(localStorage.getItem(FILE_MESURE_KEY) || "[]"); } catch (e) { return; }
      if (!f.length || navigator.onLine === false) return;
      const reste = f.filter((corps) => {
        try { return !navigator.sendBeacon?.(MESURE_URL, new Blob([JSON.stringify(corps)], { type: "text/plain" })); }
        catch (e) { return true; }
      });
      try { localStorage.setItem(FILE_MESURE_KEY, JSON.stringify(reste)); } catch (e) { /* */ }
    }
    if (reglageBorne) {
      window.addEventListener("online", videFileMesure);
      videFileMesure();
    }
    // Étape du parcours, comptée par lieu (aucune donnée personnelle).
    function mesure(type) {
      const lieu = lieuCourant();
      if (!lieu) return;
      envoieMesure({
        type, lieu,
        mode: reglageBorne?.mode || "",
        vue: reglageBorne ? "borne" : "web",
        test: !!navigator.webdriver
      });
    }
    // Côté téléphone : une seule fois par visite (un rechargement ne recompte pas).
    function mesureUneFois(type) {
      const k = "mm-mesure-" + type;
      try { if (sessionStorage.getItem(k)) return; sessionStorage.setItem(k, "1"); } catch (e) { /* */ }
      mesure(type);
    }
    // Un résultat rouvert sur le téléphone depuis la borne n'est pas une
    // nouvelle révélation : elle a déjà été comptée sur la borne.
    let revelationDejaComptee = false;

    function trackRevelation() {
      try {
        const o = currentBracelet?.official;
        if (!o) return;
        const key = o.profile || "";
        if (!key || key === revelationTracked) return;
        revelationTracked = key;
        if (revelationDejaComptee) { revelationDejaComptee = false; return; }
        window.Shopify?.analytics?.publish?.("mm_revelation", {
          profil: key,
          signe: currentBracelet?.profile?.zodiacSlug || "",
          chemin_de_vie: String(currentBracelet?.profile?.lifePath ?? ""),
          bracelet: o.braceletName || "",
          vue: sectionRoot?.dataset.borne === "1" ? "borne" : "web"
        });
        /* Compteur anonyme (03/10) : envoyé DIRECTEMENT au relais Cloudflare,
           qui ne garde qu'un total par jour (site / borne / signe). Aucun
           cookie, rien de lu ni d'écrit sur l'appareil, aucune donnée
           personnelle : il compte aussi la borne, où le bandeau de cookies est
           masqué et où un pixel Shopify ne se déclencherait jamais.
           `navigator.webdriver` = robot de test (preview-shot) : compté à part. */
        envoieMesure({
          type: "revelation",
          signe: currentBracelet?.profile?.zodiacSlug || "",
          vue: sectionRoot?.dataset.borne === "1" ? "borne" : "web",
          // Borne itinérante ou visiteur venu d'un lieu : la révélation compte
          // aussi pour ce lieu (vide sinon, comme avant).
          lieu: lieuCourant(),
          mode: reglageBorne?.mode || "",
          test: !!navigator.webdriver
        });
      } catch (e) {
        /* la mesure ne doit jamais empêcher la révélation */
      }
    }

    function showResultStep() {
      setStepTracker("result");
      syncStickyPrice();
      clearLoadingTimers();
      stepForm?.classList.remove("mm-life__step--active");
      stepForm?.classList.add("mm-life__step--hidden");
      stepLoading?.classList.remove("mm-life__step--active");
      stepLoading?.classList.add("mm-life__step--hidden");
      stepResult?.classList.remove("mm-life__step--hidden");
      stepResult?.classList.add("mm-life__step--active");
      scrollToSectionTop();
      const resultAnnounce = document.getElementById("lp-result-announce");
      if (resultAnnounce) {
        resultAnnounce.textContent = "Votre Bracelet d’Alignement est révélé. Faites défiler pour découvrir votre profil.";
      }
      trackRevelation();
      requestAnimationFrame(() => setupFinalizeObserver());
    }

    function showFormStep(opts) {
      const preserveFormError = !!(opts && opts.preserveFormError);
      teardownFinalizeObserver();
      setStepTracker("form");
      clearLoadingTimers();
      showInlineMessage("lp-cart-error", "");
      if (!preserveFormError) showInlineMessage("lp-form-error", "");
      stepLoading?.classList.remove("mm-life__step--active");
      stepLoading?.classList.add("mm-life__step--hidden");
      stepResult?.classList.remove("mm-life__step--active");
      stepResult?.classList.add("mm-life__step--hidden");
      stepForm?.classList.remove("mm-life__step--hidden");
      stepForm?.classList.add("mm-life__step--active");
      scrollToSectionTop();
    }

    function generateBraceletFromForm() {
      showInlineMessage("lp-form-error", "");
      showLoadingStep();

      const profilesUrl = sectionRoot?.dataset.officialProfilesUrl
        || window.MM_OFFICIAL_PROFILES_URL
        || "";
      loadProfileCareFromAsset(sectionRoot?.dataset.profileCareUrl || "");  // préchauffe (non bloquant)

      const run = () => {
        setTimeout(() => {
          try {
            const data = computeBraceletData();
            if (!data) {
              showFormStep({ preserveFormError: true });
              return;
            }
            renderResult(data, { animateIn: true });
            showResultStep();
          } catch (err) {
            console.error("[Mystic Moon] Échec après révélation :", err);
            showFormStep({ preserveFormError: true });
            showInlineMessage(
              "lp-form-error",
              "Nous n’avons pas pu afficher votre création. Réessayez, ou contactez Mystic Moon si cela persiste."
            );
          }
        }, 1200);
      };

      loadOfficialProfilesFromAsset(profilesUrl).then(result => {
        if (result && result.ok === false) {
          showFormStep({ preserveFormError: true });
          showInlineMessage(
            "lp-form-error",
            "Le chargement a échoué. Vérifiez votre connexion, puis réessayez dans un instant."
          );
          return;
        }
        run();
      });
    }

    // Tags du Customer Shopify portant le lead — relus par le poller PC (mode
    // --leads). contact[tags] est le SEUL canal de données du form natif
    // {% form 'customer' %} (pas de métafield possible sans serveur). La virgule
    // sépare les tags → on la retire du prénom. mm-dob au format ISO AAAA-MM-JJ ;
    // le poller régénère le teaser depuis prénom + date via generate_for_customer.
    function buildLeadTags() {
      const tags = ["mm-lead"];
      if (!currentBracelet) return tags.join(", ");
      const fn = capitalizeFirstname(
        firstnamesInput?.value.trim() || currentBracelet.formData?.firstnames || ""
      ).replace(/,/g, " ").trim();
      if (fn) tags.push(`mm-fn:${fn}`);
      const iso = birthdateToIso(parseBirthdate(currentBracelet.formData?.birthdate));
      if (iso) tags.push(`mm-dob:${iso}`);
      // Une famille, plusieurs dates, même adresse (Mike, 10/10) : Shopify ajoute
      // ces tags à une fiche existante, le poller envoie une lecture par date
      // (send_borne_lecture_email.traite_lectures_en_file). La 1re date passe
      // par l'aperçu, la file l'attend puis la saute (journal par date).
      if (iso) tags.push("mm-lecture-a-envoyer", `mm-lecture-dem:${iso}:${fn.replace(/:/g, " ")}:${Math.floor(Date.now() / 1000)}`);
      // Lead venu d'un lieu (QR de la borne itinérante ou carte du lieu).
      const lieu = lieuVisite();
      if (lieu) tags.push(`mm-lieu-${lieu}`);
      return tags.join(", ");
    }

    function setLeadMessage(message, type = "info") {
      if (!leadMessage) return;
      leadMessage.textContent = message;
      leadMessage.dataset.type = type;
      leadMessage.hidden = false;
    }

    // Remplit contact[tags] juste avant la soumission native du form customer.
    function fillLeadTags() {
      const tagsInput = document.getElementById("lp-lead-tags");
      if (tagsInput) tagsInput.value = buildLeadTags();
    }

    // ── Réhydratation (B) : le store EXIGE le captcha côté serveur (data-nocaptcha
    // rejeté « Missing CAPTCHA token »), donc pas d'AJAX possible — on assume la
    // soumission native + le reload (?customer_posted=true). On persiste les
    // saisies AVANT l'envoi (notre listener form s'exécute avant hCaptcha) et on
    // réhydrate le résultat au rechargement → l'utilisateur retrouve sa lecture.
    const LEAD_RESTORE_KEY = "mm-lead-restore";

    function persistLeadRestoreState() {
      try {
        sessionStorage.setItem(LEAD_RESTORE_KEY, JSON.stringify({
          day: bdayDay?.value || "",
          month: bdayMonth?.value || "",
          year: bdayYear?.value || "",
          firstname: firstnameInputForm?.value || "",
          giftMode: sectionRoot?.querySelector('input[name="gift_mode"]:checked')?.value || "self",
          giftFrom: giftFromInput?.value || "",
          giftMessage: giftMessageInput?.value || ""
        }));
      } catch (e) { /* sessionStorage indispo : repli best-effort */ }
    }

    function clearLeadRestoreState() {
      try { sessionStorage.removeItem(LEAD_RESTORE_KEY); } catch (e) {}
    }

    function restoreLeadStateIfNeeded() {
      let raw;
      try { raw = sessionStorage.getItem(LEAD_RESTORE_KEY); } catch (e) { return; }
      if (!raw) return;
      // On ne restaure QUE si la soumission a réussi (marqueur Shopify
      // ?customer_posted=true). Sinon (reload manuel, challenge annulé) on
      // LAISSE l'état en place — on ne purge qu'au moment où on restaure vraiment
      // (évite de détruire l'état sur un reload sans marqueur : pas de 2ᵉ chance).
      const posted = new URLSearchParams(window.location.search).get("customer_posted") === "true";
      if (!posted) return;
      clearLeadRestoreState();                 // consommé une seule fois (on restaure)
      let s;
      try { s = JSON.parse(raw); } catch (e) { return; }

      if (bdayDay) bdayDay.value = s.day || "";
      if (bdayMonth) bdayMonth.value = s.month || "";
      if (bdayYear) bdayYear.value = s.year || "";
      if (firstnameInputForm) firstnameInputForm.value = s.firstname || "";
      const giftRadio = sectionRoot?.querySelector(`input[name="gift_mode"][value="${s.giftMode || "self"}"]`);
      if (giftRadio) { giftRadio.checked = true; updateGiftCardsState(); toggleGiftFields(); }
      if (giftFromInput) giftFromInput.value = s.giftFrom || "";
      if (giftMessageInput) giftMessageInput.value = s.giftMessage || "";

      const data = computeBraceletData();
      if (!data) return;                       // saisies invalides : on reste à l'étape form
      renderResult(data);
      showResultStep();
      try {                                    // nettoie ?customer_posted=true de l'URL
        const u = new URL(window.location.href);
        if (u.searchParams.has("customer_posted")) {
          u.searchParams.delete("customer_posted");
          window.history.replaceState({}, "", u.toString());
        }
      } catch (e) {}
      const leadFn = capitalizeFirstname(s.firstname || firstnameInputForm?.value || "");
      setLeadMessage(
        (leadFn ? `Parfait, ${leadFn} ! ` : "Parfait ! ") +
        "Votre aperçu arrive dans quelques minutes par email. " +
        "Pensez à vérifier vos spams si vous ne le voyez pas tout de suite.",
        "success"
      );
    }

    // Remonte au début de la section SOUS le menu : en remontant, l'en-tête
    // « data-scroll-up » réapparaît et recouvrait le haut du bracelet (mobile).
    function scrollToSectionTop() {
      if (!sectionRoot) return window.scrollTo({ top: 0, behavior: "smooth" });
      const stickyHeader = document.querySelector("sticky-header, .shopify-section-header-sticky, .shopify-section-header");
      const headerH = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
      const top = sectionRoot.getBoundingClientRect().top + window.scrollY - headerH;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    }

    function scrollToBraceletVisual(behavior = "smooth") {
      const target = document.getElementById("lp-slots-container");
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const stickyHeader = document.querySelector("sticky-header, .shopify-section-header-sticky, .shopify-section-header");
      const headerH = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const centerY = rect.top + window.scrollY - (viewportH - rect.height) / 2;
      const topAlignedY = rect.top + window.scrollY - headerH - 24;
      let y = Math.min(centerY, topAlignedY);
      const maxY = (document.documentElement.scrollHeight || 0) - viewportH;
      y = Math.max(0, Math.min(y, Math.max(0, maxY)));
      window.scrollTo({ top: y, behavior });
    }

    // ?gift=1 : la page « Offrir » (templates/page.offrir.json) amène ici un
    // visiteur qui a DÉJÀ choisi d'offrir. Sans ce pré-armement il devrait
    // refaire le choix qu'il vient de faire. Le paramètre ne fait que cocher la
    // carte : toute restauration d'état ultérieure (retour du lead form, plus
    // bas) la remplace, l'intention explicite du visiteur primant sur l'URL.
    function applyGiftUrlParam() {
      let wanted = null;
      try {
        wanted = new URLSearchParams(window.location.search).get("gift");
      } catch (error) {
        return;
      }
      if (wanted !== "1") return;
      const giftRadio = sectionRoot?.querySelector('input[name="gift_mode"][value="gift"]');
      if (!giftRadio || giftRadio.checked) return;
      giftRadio.checked = true;
    }

    setDefaultVariantSize();
    applyGiftUrlParam();
    toggleGiftFields();
    updateGiftCardsState();
    attachDateAutoFocus();
    attachGiftCardsListener();
    renderReviews();  // bloc avis atelier (global, indépendant du profil)
    firstnameInputForm?.addEventListener("blur", () => {
      firstnameInputForm.value = capitalizeFirstname(firstnameInputForm.value);
    });
    giftModeInputs.forEach(input => input.addEventListener("change", () => {
      updateGiftCardsState();
      toggleGiftFields();
    }));

    const profilesUrl = sectionRoot?.dataset.officialProfilesUrl
      || window.MM_OFFICIAL_PROFILES_URL
      || "";
    /* ARRIVÉE DEPUIS LA BORNE ITINÉRANTE (07/10), côté téléphone.
       ?lieu=<id>   : carte ou QR générique d'un lieu (ou QR de secours de la
                      borne sans réseau, marqué qr=1) → lieu gardé pour la visite.
       ?r=<code>    : QR personnel → le Worker rend prénom + date (code valable
                      24 h) et le résultat s'ouvre déjà révélé, à son prénom. */
    function noteLieuDepuisUrl() {
      if (sectionRoot?.dataset.borne === "1") return;
      let q;
      try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
      const l = lieuOk(q.get("lieu"));
      if (!l) return;
      try { sessionStorage.setItem(LIEU_SESSION_KEY, l); } catch (e) { /* */ }
      if (!q.get("r")) mesureUneFois(q.get("qr") === "1" ? "qr_scanne" : "carte_scannee");
    }
    async function restoreFromBorneCode() {
      if (sectionRoot?.dataset.borne === "1") return;
      let code, depuisEmail = false;
      try {
        const q = new URLSearchParams(window.location.search);
        code = q.get("r") || "";
        // Lien « Voir mon bracelet » d'un e-mail (10/10) : même code, mais ce
        // n'est pas un scan de QR, il ne doit pas gonfler ce compteur.
        depuisEmail = q.get("src") === "email";
      } catch (e) { return; }
      if (!/^[A-Za-z0-9]{8}$/.test(code)) return;
      // Le code ne reste pas dans l'adresse : ni historique, ni partage du lien.
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("r");
        window.history.replaceState({}, "", u.toString());
      } catch (e) { /* */ }
      let d;
      try {
        const r = await fetch(`${WORKER_URL}/resultat/${encodeURIComponent(code)}`, { cache: "no-store" });
        d = await r.json().catch(() => ({}));
        if (!r.ok || !d.ok) d = null;
      } catch (e) { d = null; }
      if (!d) {
        showInlineMessage("lp-form-error", "Ce lien n’est plus valable. Indiquez à nouveau votre prénom et votre date de naissance : votre bracelet sera le même.");
        return;
      }
      const l = lieuOk(d.lieu);
      if (l) { try { sessionStorage.setItem(LIEU_SESSION_KEY, l); } catch (e) { /* */ } }
      if (bdayDay) bdayDay.value = d.jour || "";
      if (bdayMonth) bdayMonth.value = d.mois || "";
      if (bdayYear) bdayYear.value = d.annee || "";
      if (firstnameInputForm) firstnameInputForm.value = d.prenom || "";
      const data = computeBraceletData();
      if (!data) return;
      revelationDejaComptee = true;
      renderResult(data);
      showResultStep();
      if (!depuisEmail) mesureUneFois("qr_scanne");
    }
    noteLieuDepuisUrl();

    loadOfficialProfilesFromAsset(profilesUrl).then(() => {
      restoreLeadStateIfNeeded();
      restoreFromBorneCode();
    });

    form?.addEventListener("submit", event => {
      event.preventDefault();
      generateBraceletFromForm();
    });

    variantInputs.forEach(input => {
      input.addEventListener("change", () => {
        if (!currentBracelet) return;
        const size = input.dataset.value || DEFAULT_WRIST_SIZE;
        const data = computeBraceletData(size);
        if (!data) return;
        renderResult(data);
      });
    });

    backToFormBtn?.addEventListener("click", showFormStep);

    // Lien doré « Voir mon coffret ↓ » de la zone lecture : scroll doux vers
    // le bloc commande (délégation, le lien est (re)généré à chaque render).
    sectionRoot?.addEventListener("click", event => {
      const ctaLink = event.target.closest(".mm-life__reading-cta-link");
      if (!ctaLink) return;
      event.preventDefault();
      document.getElementById("lp-commerce")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    // Libellé « Expédition » du parcours en ligne. En borne il est remplacé :
    // la logistique du bracelet borne (retrait en boutique ou expédition) n'est
    // pas tranchée, et promettre une date au comptoir engagerait Mike.
    const EXPEDITION_EN_LIGNE = "Expédiée sous 24 à 48 h ouvrées";
    const EXPEDITION_BORNE = "Demande borne · à encaisser en caisse";

    /**
     * Propriétés de ligne du bracelet — SOURCE UNIQUE.
     *
     * Utilisée par l'ajout au panier ET par l'envoi en caisse depuis la borne.
     * Ne surtout pas dupliquer : c'est cette charge que le pipeline de
     * fabrication relit (`parse_order_to_struct`), et deux constructions
     * parallèles finiraient par diverger — la borne produirait alors des
     * demandes infabricables, ce qui ne se verrait qu'à l'atelier.
     */
    function buildBraceletProperties(selectedVariant, expedition) {
      const o = currentBracelet.official;
      const stones = currentBracelet.selectedStones;
      const p1 = stones[0]?.metaStoneName || stones[0]?.name || "";
      const p2 = stones[1]?.metaStoneName || stones[1]?.name || "";
      const p3 = stones[2]?.metaStoneName || stones[2]?.name || "";
      const birthDisp = formatBirthdateDisplay(parseBirthdate(currentBracelet.formData.birthdate));

      return {
        "Expédition": expedition,
        "Profil": o.profile || "",
        "Bracelet": o.braceletName || "",
        "Signe": currentBracelet.profile.zodiacLabel || "",
        "Chemin de vie": displayNumber(currentBracelet.profile.lifePath),
        "Pierre 1": p1,
        "Pierre 2": p2,
        "Pierre 3": p3,
        "Perle gravée Mystic Moon": FINISH_LABELS[currentFinish] || FINISH_LABELS.argent,
        "Interperles en hématite": SPACER_LABELS[spacersOn],
        "Affirmation": o.affirmation || "",
        "Palette visuelle": o.visualPalette || "",
        "Rendu émotionnel": o.emotionalRendering || "",
        "Prénom": currentBracelet.formData?.firstnames || "",
        "Date de naissance": birthDisp,
        "Mode cadeau": currentBracelet.giftData?.isGift ? "oui" : "non",
        ...(currentBracelet.giftData?.isGift ? {
          "De la part de": currentBracelet.giftData.from || "",
          "Message cadeau": currentBracelet.giftData.message || ""
        } : {}),
        "_config": JSON.stringify({
          profileKey: currentBracelet.profileKey,
          profile: currentBracelet.profile,
          official: o,
          size: selectedVariant.dataset.value,
          finish: currentFinish,
          spacers: spacersOn,
          // Anneau affiché (sens horaire depuis l'interperle) ; repli, s'il n'est pas
          // (encore) dessiné, sur la séquence qui correspond au choix des rondelles.
          beads: lastRingBeads || (spacersOn ? buildSpacedSequence(currentBracelet) : currentBracelet.sequence || [])
            .filter(s => !s.spacer)
            .map(s => s.metaStoneName || s.name || ""),
          stonesShopify: stones.map(s => ({
            name: s.metaStoneName || s.name,
            id: s.id,
            shopifyMissing: !!s.shopifyMissing
          })),
          gift: currentBracelet.giftData || { mode: "self", isGift: false, from: "", message: "" }
        })
      };
    }

    addToCartBtn?.addEventListener("click", async () => {
      showInlineMessage("lp-cart-error", "");
      if (!currentBracelet) {
        showInlineMessage("lp-cart-error", "Révélez d’abord votre création avant de commander.");
        return;
      }
      const selectedVariant = sectionRoot?.querySelector('input[name="id"]:checked');
      if (!selectedVariant) {
        showInlineMessage("lp-cart-error", "Choisissez votre taille de poignet avant d’ajouter au panier.");
        return;
      }
      const originalText = addToCartBtn.textContent;
      try {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = "Ajout en cours...";

        // Commande venue d'un lieu (borne itinérante, carte) : attribut de
        // COMMANDE caché, lu par le relevé. Pas une propriété de ligne : le
        // pipeline de fabrication n'en voit rien.
        const lieu = lieuVisite();
        if (lieu) {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: { _lieu: lieu } })
          }).catch(() => { /* l'attribution ne bloque jamais l'achat */ });
        }

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: Number(selectedVariant.value),
            quantity: 1,
            properties: buildBraceletProperties(selectedVariant, EXPEDITION_EN_LIGNE)
          })
        });

        if (!response.ok) throw new Error("Erreur ajout panier");

        addToCartBtn.textContent = "Ajouté";
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        document.dispatchEvent(new CustomEvent("cart:open"));

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) {
          cartDrawer.open = true;
          cartDrawer.setAttribute("open", "");
          cartDrawer.classList.add("active");
        }

        const cartIcon = document.querySelector('[href="/cart"], .header__icon--cart');
        if (cartIcon) cartIcon.click();
      } catch (error) {
        console.error(error);
        showInlineMessage("lp-cart-error", "L’ajout au panier a échoué. Réessayez ou contactez-nous.");
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = originalText || "Commander mon coffret";
      }
    });

    /* ============================================================
       MODE BORNE BOUTIQUE (?view=borne-bracelet)
       Pas de panier : la demande part vers le relais local, qui crée la
       commande provisoire. L'encaissement se fait en caisse.
       ============================================================ */
    if (sectionRoot?.dataset.borne === "1") {
      // On attend une BASE (http://hôte:port). Le relais, lui, affiche au
      // démarrage l'URL complète « …/demande » : collée telle quelle dans le
      // réglage, elle donnerait « /demande/sante ». On la tolère.
      const relayUrl = (sectionRoot.dataset.borneRelayUrl || "").trim()
        .replace(/\/+$/, "").replace(/\/demande$/, "");
      const relayToken = (sectionRoot.dataset.borneRelayToken || "").trim();
      const sendBtn = document.getElementById("lp-send-to-desk");
      const statusEl = document.getElementById("lp-borne-status");
      const doneZone = document.getElementById("lp-borne-done");
      const numEl = document.getElementById("lp-borne-num");
      const borneForm = document.getElementById("lp-borne-form");
      const addressZone = document.getElementById("lp-borne-address");
      const relayHeaders = relayToken ? { "X-Borne-Token": relayToken } : {};
      const champ = (id) => (document.getElementById(id)?.value || "").trim();

      const setBorneStatus = (msg) => {
        if (!statusEl) return;
        statusEl.textContent = msg || "";
        statusEl.hidden = !msg;
      };

      /* RÉCEPTION (Mike, 02/10) : boutique, point relais (offert) ou domicile
         Colissimo (6,90 €). Le total « à régler en caisse » suit le choix :
         c'est le premier verrou contre une livraison oubliée à l'encaissement
         (la cliente arrive au comptoir en annonçant le bon montant). */
      const RECEPTION_NOTES = {
        boutique: "Nous vous prévenons dès qu’il vous attend à la boutique.",
        relais: "Livraison offerte dans un point relais Mondial Relay près de chez vous.",
        domicile: "Livré chez vous par Colissimo."
      };
      const totalEl = document.getElementById("lp-borne-total");
      const baseCents = parseInt(totalEl?.dataset.base || "0", 10) || 0;
      const euros = (cents) => (cents / 100).toFixed(2).replace(".", ",") + " €";
      const receptionInput = () => sectionRoot.querySelector('input[name="mm-reception"]:checked');
      const reception = () => receptionInput()?.value || "boutique";
      const totalCents = () => baseCents + (parseInt(receptionInput()?.dataset.prix || "0", 10) || 0);
      const receptionLabel = () => {
        const i = receptionInput();
        return i ? (sectionRoot.querySelector(`label[for="${i.id}"]`)?.textContent || "").trim() : "";
      };
      const syncReception = () => {
        const r = reception();
        const t = euros(totalCents());
        if (totalEl) totalEl.textContent = t;
        const t2 = document.getElementById("lp-borne-total-2");
        if (t2) t2.textContent = t;
        const note = document.getElementById("lp-reception-note");
        if (note) note.textContent = RECEPTION_NOTES[r] || "";
        if (addressZone) addressZone.hidden = r === "boutique";
        // Téléphone : livraison seulement, pour le transporteur (Mike, 03/10).
        const phoneBlock = document.getElementById("lp-borne-phone-block");
        if (phoneBlock) phoneBlock.hidden = r === "boutique";
        if (r === "boutique") { const ph = document.getElementById("lp-borne-phone"); if (ph) ph.value = ""; }
        majTexteContact(r);
      };
      /* Dernière page (Mike, 10/10) : plus de « Recevez votre lecture » en doublon
         de l'écran « Emportez votre lecture ». Lecture déjà envoyée là-bas : on ne
         la repromet pas. Sinon cette page reste la dernière chance de la recevoir. */
      function majTexteContact(r) {
        r = r || document.querySelector('input[name="mm-reception"]:checked')?.value || "boutique";
        const dejaLecture = sectionRoot.dataset.emportOk === "1";
        // Choix des nouvelles déjà fait sur l'écran Emporter (Mike, 10/10) : on ne
        // repose pas la question au moment de payer, on reprend sa réponse.
        const optout = document.getElementById("lp-borne-optout");
        const optoutRow = optout?.closest("label");
        if (optoutRow) optoutRow.hidden = dejaLecture;
        if (optout && dejaLecture) optout.checked = sectionRoot.dataset.emportNouvelles !== "1";
        if (optout && !dejaLecture && optout.dataset.reprisEmporter === "1") optout.checked = false;
        if (optout) optout.dataset.reprisEmporter = dejaLecture ? "1" : "";
        const title = document.getElementById("lp-contact-title");
        if (title) title.textContent = r === "boutique" ? "Pour vous prévenir" : "Où vous livrer ?";
        const intro = document.getElementById("lp-contact-intro");
        if (!intro) return;
        if (r === "boutique") {
          intro.textContent = dejaLecture
            ? "Nous vous écrivons dès que votre bracelet est prêt."
            : "Nous vous écrivons dès que votre bracelet est prêt, avec votre lecture et l’entretien de vos pierres.";
        } else {
          intro.textContent = dejaLecture
            ? "Nous vous envoyons par e-mail le suivi de votre colis."
            : "Nous vous envoyons par e-mail votre lecture, l’entretien de vos pierres, puis le suivi de votre colis.";
        }
      }
      /* Relance douce (03/10) : envoyer sans e-mail passe par un écran qui dit
         ce que la cliente perd, avec « Ajouter mon e-mail » en premier. Jamais
         bloquant : « Continuer sans » envoie quand même. */
      let sansEmailOk = false;
      const noEmail = document.getElementById("lp-borne-noemail");
      document.getElementById("lp-borne-add-email")?.addEventListener("click", () => {
        if (noEmail) noEmail.hidden = true;
        if (borneForm) borneForm.hidden = false;
        document.getElementById("lp-borne-email")?.focus();
      });
      document.getElementById("lp-borne-continue")?.addEventListener("click", () => {
        sansEmailOk = true;
        if (noEmail) noEmail.hidden = true;
        if (borneForm) borneForm.hidden = false;
        sendBtn?.click();
      });
      document.getElementById("lp-reception")?.addEventListener("change", syncReception);
      syncReception();

      const collectContact = () => ({
        email: champ("lp-borne-email"),
        phone: champ("lp-borne-phone"),
        optout: !!document.getElementById("lp-borne-optout")?.checked
      });
      const collectAddress = () => ({
        nom: champ("lp-borne-nom"),
        adresse: champ("lp-borne-adresse"),
        cp: champ("lp-borne-cp"),
        ville: champ("lp-borne-ville")
      });
      // Une livraison sans adresse ni moyen de joindre la cliente serait un colis
      // impossible à préparer : on le dit AVANT l'envoi, à l'écran.
      const contactError = () => {
        const c = collectContact();
        if (c.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)) return "Votre e-mail semble incomplet.";
        if (reception() === "boutique") return "";
        const a = collectAddress();
        if (!a.nom || !a.adresse || !a.cp || !a.ville) return "Indiquez votre adresse complète pour la livraison.";
        if (!c.email && !c.phone) return "Indiquez un e-mail ou un téléphone pour suivre votre colis.";
        return "";
      };

      /* Repli « comptoir » : sans relais (ou relais injoignable), le bouton ne
         reste pas grisé. Il affiche la commande en clair pour qu'elle soit
         saisie en caisse (« Livret sans commande »). Le 02/10, une cliente a
         touché le bouton grisé : rien n'est parti, la demande a été perdue. */
      let comptoirMode = false;
      const enableComptoir = () => {
        comptoirMode = true;
        if (sendBtn) {
          sendBtn.disabled = false;
          sendBtn.textContent = "Montrer ma commande au comptoir";
        }
        setBorneStatus("");
      };
      const showComptoirRecap = () => {
        const selectedVariant = sectionRoot.querySelector('input[name="id"]:checked');
        const props = buildBraceletProperties(selectedVariant || { dataset: {} }, EXPEDITION_BORNE);
        const c = collectContact();
        const a = collectAddress();
        const rows = [
          ["Prénom", props["Prénom"]],
          ["Date de naissance", props["Date de naissance"]],
          ["Bracelet", props["Bracelet"]],
          ["Taille", selectedVariant?.dataset.value ? selectedVariant.dataset.value + " cm" : ""],
          ["Interperles en hématite", props["Interperles en hématite"]],
          ...(props["Mode cadeau"] === "oui" ? [
            ["Pour offrir, de la part de", props["De la part de"]],
            ["Message", props["Message cadeau"]]
          ] : []),
          ["Réception", receptionLabel()],
          ...(reception() !== "boutique" ? [["Adresse", [a.nom, a.adresse, [a.cp, a.ville].filter(Boolean).join(" ")].filter(Boolean).join(", ")]] : []),
          ["E-mail", c.email],
          ["Téléphone", c.phone],
          ["À régler en caisse", euros(totalCents())]
        ].filter(([, v]) => v);
        const list = document.getElementById("lp-borne-recap-list");
        if (list) {
          list.innerHTML = rows.map(([k, v]) =>
            `<dt>${escapeHtml(k)}</dt><dd>${escapeHtml(v)}</dd>`).join("");
        }
        if (borneForm) borneForm.hidden = true;
        if (checkoutHint) checkoutHint.hidden = true;
        const recap = document.getElementById("lp-borne-recap");
        if (recap) recap.hidden = false;
      };

      if (!relayUrl) {
        enableComptoir();
      } else {
        fetch(relayUrl + "/sante", { method: "GET", headers: relayHeaders })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error("relais KO"))))
          .then((sante) => {
            if (sendBtn) sendBtn.disabled = false;
            // En marche à blanc le relais ne crée RIEN : sans cet avertissement,
            // la cliente repartirait au comptoir avec un numéro qui n'existe pas.
            setBorneStatus(sante && sante.dry_run
              ? "Mode test : aucune demande ne sera réellement créée."
              : "");
          })
          .catch((e) => {
            console.warn("[borne] relais injoignable :", e);
            enableComptoir();
          });
      }

      sendBtn?.addEventListener("click", async () => {
        showInlineMessage("lp-borne-error", "");
        if (!currentBracelet) {
          showInlineMessage("lp-borne-error", "Révélez d’abord votre création.");
          return;
        }
        const erreurContact = contactError();
        if (erreurContact) { showInlineMessage("lp-borne-error", erreurContact); return; }
        if (!collectContact().email && !sansEmailOk && noEmail) {
          if (borneForm) borneForm.hidden = true;
          noEmail.hidden = false;
          return;
        }
        if (comptoirMode) { showComptoirRecap(); return; }
        const selectedVariant = sectionRoot.querySelector('input[name="id"]:checked');
        if (!selectedVariant) {
          showInlineMessage("lp-borne-error", "Choisissez votre taille de poignet avant d’envoyer votre commande.");
          return;
        }

        const originalText = sendBtn.textContent;
        sendBtn.disabled = true;
        sendBtn.textContent = "Envoi en cours…";
        try {
          const response = await fetch(relayUrl + "/demande", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...relayHeaders },
            body: JSON.stringify({
              variant_id: Number(selectedVariant.value),
              size: selectedVariant.dataset.value,
              properties: buildBraceletProperties(selectedVariant, EXPEDITION_BORNE),
              reception: reception(),
              contact: collectContact(),
              address: reception() === "boutique" ? null : collectAddress(),
              lieu: reglageBorne?.lieu || ""
            })
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data.ok) throw new Error(data.error || "envoi refusé");

          // Le numéro affiché est le `name` de la commande provisoire Shopify
          // (ex. #D47) : aucun code maison à inventer, on le retrouve tel quel au
          // comptoir. ⚠️ Une réponse SANS numéro est traitée comme un échec : un
          // paragraphe vide est retiré du rendu par Be Yours (`p:empty`, base.css),
          // et l'écran afficherait « Présentez ce numéro » sans aucun numéro.
          if (!data.name) throw new Error("réponse sans numéro de demande");
          if (numEl) numEl.textContent = data.name;
          const due = document.getElementById("lp-borne-due");
          if (due) due.textContent = data.total ? euros(Math.round(parseFloat(data.total) * 100)) : euros(totalCents());

          /* La demande est partie : on fige la commande. Sans cela, changer de
             taille ou de réception continuerait de mettre l'écran à jour alors
             que le bon porte l'ancienne : l'atelier fabriquerait autre chose
             que ce que la cliente a sous les yeux. */
          sectionRoot.querySelectorAll("#lp-finalize-panel input").forEach((input) => {
            input.disabled = true;
          });
          if (borneForm) borneForm.hidden = true;
          setBorneStatus("");
          if (checkoutHint) checkoutHint.hidden = true;
          if (doneZone) doneZone.hidden = false;
          // Appareil partagé : l'écran de confirmation ne reste pas 5 minutes
          // avec le prénom et le numéro. 90 s pour aller au comptoir.
          setTimeout(() => resetBorne(), 90000);
        } catch (error) {
          // Le détail reste en console : les messages du relais parlent d'atelier
          // et de configuration, ce n'est pas ce qu'on montre à une cliente.
          console.error("[borne] envoi en caisse :", error);
          showInlineMessage("lp-borne-error", "L’envoi n’a pas abouti. Faites-nous signe : nous prenons le relais.");
          sendBtn.disabled = false;
          sendBtn.textContent = originalText || "Envoyer ma commande en caisse";
        }
      });

      /* RÉSULTAT EN ÉCRANS (borne seulement, Mike 02/10). Debout devant une
         tablette on touche, on ne lit pas une longue page : le résultat se
         parcourt écran par écran (Suivant / Retour, glisser du doigt, points de
         progression). Les blocs existants servent tels quels d'écrans : aucun
         n'est déplacé dans le DOM, le CSS (borne-chrome.css) masque les autres.
         1 révélation · 2 qui vous êtes · 3 ce qui vous freine · 4 les trois
         pierres · 5 la commande (taille, réception, total) · 6 les coordonnées et l'envoi. La lecture passe AVANT les pierres (Mike,
         02/10) : le portrait pose le besoin, les pierres y répondent. Les
         écrans 2 et 3 partagent le même bloc de lecture : la classe
         `mm-deck--lecture-<partie>` du section dit quelle partie montrer.
         Contenu identique au site public : la borne n'en montre pas moins. */
      /* BORNE ITINÉRANTE (07/10) : le mode pilote le parcours, une seule base de
         code. « avec » vendeur = parcours caisse actuel + sortie « Continuer sur
         mon téléphone » ; « sans » vendeur = la borne s'arrête au QR personnel
         (aucune coordonnée tapée sur un écran public). Sans réglage : inchangé. */
      if (reglageBorne) {
        sectionRoot.dataset.borneMode = reglageBorne.mode;
        sectionRoot.classList.add("mm-borne-itinerante");
        let debutCompte = false;
        [firstnameInputForm, bdayDay, bdayMonth, bdayYear].forEach((el) => el?.addEventListener("input", () => {
          if (debutCompte) return;
          debutCompte = true;
          mesure("debut");
        }));
      }
      const qrZone = reglageBorne ? sectionRoot.querySelector(".mm-life__result-zone--borne-qr") : null;
      const sansVendeur = !!qrZone && reglageBorne.mode === "sans";
      let surEcranQr = false;

      const readingZone = sectionRoot.querySelector(".mm-life__result-zone--reading");
      let deckSlides = [
        sectionRoot.querySelector(".mm-life__hero"),
        readingZone,
        readingZone,
        sectionRoot.querySelector(".mm-life__result-zone--stones"),
        sectionRoot.querySelector(".mm-life__result-zone--shop"),
        // 6 : coordonnées + envoi en caisse (Mike, 02/10)
        sectionRoot.querySelector(".mm-life__result-zone--borne-contact")
      ];
      let deckReadingPart = [null, "portrait", "freine", null, null, null];
      // « Emportez votre lecture » (Mike, 10/10) : l'e-mail AVANT la taille et le
      // prix, juste après les pierres. Absent en sans vendeur (pas de saisie).
      const emportZone = sansVendeur ? null : sectionRoot.querySelector(".mm-life__result-zone--borne-emporter");
      if (emportZone) {
        deckSlides.splice(4, 0, emportZone);
        deckReadingPart.splice(4, 0, null);
      }
      // Zones commande + coordonnées : hors du carrousel en mode sans vendeur, donc
      // jamais touchées par `.mm-deck__slide:not(.is-current)` → masquées à la main.
      const zonesCaisse = [sectionRoot.querySelector(".mm-life__result-zone--shop"),
        sectionRoot.querySelector(".mm-life__result-zone--borne-contact")];
      if (qrZone) {
        if (sansVendeur) {
          // Sans vendeur : ni commande ni coordonnées sur la borne.
          deckSlides = deckSlides.slice(0, 4).concat(qrZone);
          deckReadingPart = deckReadingPart.slice(0, 4).concat(null);
        } else {
          deckSlides = deckSlides.concat(qrZone);
          deckReadingPart = deckReadingPart.concat(null);
        }
      }
      const qrIdx = qrZone ? deckSlides.indexOf(qrZone) : -1;
      // Dernier écran où « Suivant » s'affiche : avec vendeur, l'écran QR est une
      // sortie latérale (bouton de l'écran commande), pas la suite des coordonnées.
      const dernierSuivant = qrZone && !sansVendeur ? deckSlides.length - 2 : deckSlides.length - 1;

      /* QR PERSONNEL : le Worker garde prénom + date 24 h sous un code
         aléatoire ; le QR ne porte que ce code. Sans réseau, QR de secours
         sans aucune donnée (le téléphone redemandera la date). */
      // Un QR par écran (sortie téléphone de l'itinérante, « Emportez votre lecture ») :
      // chacun garde la saisie pour laquelle il a été dessiné.
      const qrPourSaisie = {};
      const prepareQr = async (boxId = "lp-borne-qr", statusId = "lp-borne-qr-status", compter = true) => {
        const box = document.getElementById(boxId);
        const st = document.getElementById(statusId);
        if (!box || !currentBracelet) return;
        const saisie = [firstnameInputForm?.value, bdayDay?.value, bdayMonth?.value, bdayYear?.value].join("|");
        if (qrPourSaisie[boxId] === saisie && box.innerHTML) return;
        qrPourSaisie[boxId] = saisie;
        box.innerHTML = "";
        if (st) { st.textContent = "Préparation de votre code…"; st.hidden = false; }
        // Boutique sans réglage de lieu : le QR porte « boutique-janze ».
        const lieu = reglageBorne?.lieu || "boutique-janze";
        const page = window.location.origin + window.location.pathname;
        const utm = `utm_source=borne&utm_medium=qr&utm_campaign=${encodeURIComponent(lieu)}`;
        let url = `${page}?lieu=${encodeURIComponent(lieu)}&qr=1&${utm}`;
        let secours = true;
        if (relayUrl) {
          const ctrl = new AbortController();
          const t = setTimeout(() => ctrl.abort(), 5000);
          try {
            const r = await fetch(relayUrl + "/resultat", {
              method: "POST",
              headers: { "Content-Type": "application/json", ...relayHeaders },
              body: JSON.stringify({
                prenom: (firstnameInputForm?.value || "").trim(),
                jour: bdayDay?.value || "", mois: bdayMonth?.value || "", annee: bdayYear?.value || "",
                lieu
              }),
              signal: ctrl.signal
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok && d.ok && /^[A-Z0-9]{8}$/.test(d.code || "")) {
              url = `${page}?r=${d.code}&${utm}`;
              secours = false;
            }
          } catch (e) {
            console.warn("[borne] code de résultat indisponible :", e);
          } finally { clearTimeout(t); }
        }
        // La saisie a pu changer pendant l'attente : on ne dessine pas un code périmé.
        if (qrPourSaisie[boxId] !== saisie) return;
        if (typeof window.qrcode !== "function") {
          if (st) st.textContent = "Code indisponible pour le moment. Retrouvez votre bracelet sur mystic-moon.fr avec votre prénom et votre date de naissance : ce sera le même.";
          return;
        }
        const q = window.qrcode(0, "M");
        q.addData(url);
        q.make();
        box.innerHTML = q.createSvgTag({ cellSize: 6, margin: 3, scalable: true });
        if (st) {
          st.textContent = secours
            ? "Connexion faible : sur votre téléphone, il faudra indiquer à nouveau votre prénom et votre date de naissance. Votre bracelet sera le même."
            : "";
          st.hidden = !secours;
        }
        if (compter) mesure("qr_affiche");
      };

      /* Envoi de l'e-mail : le relais crée la fiche lead, le poller envoie
         l'aperçu « Voir mon bracelet » en une minute. Un même prénom + date ne
         part qu'une fois par saisie (pas de double envoi sur un double appui). */
      const emportEmail = document.getElementById("lp-emport-email");
      const emportConsent = document.getElementById("lp-emport-consent");
      const emportSend = document.getElementById("lp-emport-send");
      const emportStatus = document.getElementById("lp-emport-status");
      let emportPourSaisie = "";
      const emportMessage = (msg) => {
        if (!emportStatus) return;
        emportStatus.textContent = msg || "";
        emportStatus.hidden = !msg;
      };
      const preparerEmport = () => {
        const saisie = [firstnameInputForm?.value, bdayDay?.value, bdayMonth?.value, bdayYear?.value].join("|");
        if (emportPourSaisie === saisie) return;
        emportPourSaisie = saisie;
        if (emportEmail) emportEmail.value = "";
        if (emportConsent) emportConsent.checked = false;
        if (emportSend) { emportSend.disabled = false; emportSend.textContent = "Recevoir ma lecture"; }
        emportMessage("");
        delete sectionRoot.dataset.emportOk;
        delete sectionRoot.dataset.emportNouvelles;
        majTexteContact();
      };
      emportSend?.addEventListener("click", async () => {
        const email = (emportEmail?.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          emportMessage("Vérifiez votre adresse e-mail : elle semble incomplète.");
          emportEmail?.focus();
          return;
        }
        if (!relayUrl) {
          emportMessage("Envoi indisponible pour le moment. Scannez le code avec votre téléphone pour retrouver votre bracelet.");
          return;
        }
        emportSend.disabled = true;
        emportSend.textContent = "Envoi…";
        emportMessage("");
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 8000);
        let ok = false, deja = false;
        try {
          const r = await fetch(relayUrl + "/lead", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...relayHeaders },
            body: JSON.stringify({
              prenom: (firstnameInputForm?.value || "").trim(),
              jour: bdayDay?.value || "", mois: bdayMonth?.value || "", annee: bdayYear?.value || "",
              email, nouvelles: !!emportConsent?.checked
            }),
            signal: ctrl.signal
          });
          const d = await r.json().catch(() => ({}));
          ok = r.ok && d.ok;
          deja = ok && !!d.deja;
        } catch (e) {
          console.warn("[borne] envoi de la lecture impossible :", e);
        } finally { clearTimeout(t); }
        if (ok) {
          // L'adresse donnée ici remplit la dernière page : rien à retaper.
          sectionRoot.dataset.emportOk = "1";
          sectionRoot.dataset.emportNouvelles = emportConsent?.checked ? "1" : "";
          const finale = document.getElementById("lp-borne-email");
          if (finale && !finale.value.trim()) finale.value = email;
          majTexteContact();
        }
        if (deja) {
          // Adresse déjà connue comme lead : rien n'est renvoyé (pas de second profil).
          emportSend.disabled = false;
          emportSend.textContent = "Recevoir ma lecture";
          emportMessage("Cette adresse a déjà reçu une lecture. Pour garder celle-ci, scannez le code avec votre téléphone.");
        } else if (ok) {
          emportSend.textContent = "Envoyé";
          emportMessage("Votre lecture arrive dans votre boîte de réception d’ici quelques minutes.");
        } else {
          emportSend.disabled = false;
          emportSend.textContent = "Recevoir ma lecture";
          emportMessage("Envoi impossible pour le moment. Réessayez, ou scannez le code avec votre téléphone.");
        }
      });

      if (deckSlides.every(Boolean)) {
        // Itinérante : l'écran QR n'apparaît que si le carrousel s'installe.
        if (qrZone) qrZone.hidden = false;
        if (emportZone) emportZone.hidden = false;
        if (sansVendeur) zonesCaisse.forEach((z) => { if (z) z.hidden = true; });
        sectionRoot.classList.add("mm-deck");
        new Set(deckSlides).forEach((s) => s.classList.add("mm-deck__slide"));
        const nav = document.createElement("nav");
        nav.className = "mm-deck__nav";
        nav.setAttribute("aria-label", "Écrans du résultat");
        nav.innerHTML =
          '<button type="button" class="button button--secondary mm-deck__prev">‹ Retour</button>'
          + '<span class="mm-deck__dots" aria-hidden="true">'
          + deckSlides.map(() => "<span></span>").join("")
          + "</span>"
          + '<button type="button" class="button mm-deck__next">Suivant ›</button>';
        sectionRoot.appendChild(nav);
        if (reglageBorne) {
          // Itinérante : « Terminer » efface tout, sur chaque écran du résultat.
          const end = document.createElement("button");
          end.type = "button";
          end.className = "button button--tertiary mm-deck__end";
          end.textContent = "Terminer";
          end.addEventListener("click", () => resetBorne());
          nav.appendChild(end);
        }
        const prevBtn = nav.querySelector(".mm-deck__prev");
        const nextBtn = nav.querySelector(".mm-deck__next");
        const dots = nav.querySelectorAll(".mm-deck__dots span");
        let deckIdx = 0;

        const goSlide = (i, fromLeft) => {
          const before = deckIdx;
          deckIdx = Math.max(0, Math.min(deckSlides.length - 1, i));
          const cur = deckSlides[deckIdx];
          new Set(deckSlides).forEach((s) => {
            s.classList.remove("is-current", "is-from-left");
          });
          // reflow : relance l'animation d'entrée même quand l'écran 3 réutilise
          // le bloc de l'écran 2
          void cur.offsetWidth;
          cur.classList.add("is-current");
          if (fromLeft) cur.classList.add("is-from-left");
          sectionRoot.classList.toggle("mm-deck--lecture-portrait", deckReadingPart[deckIdx] === "portrait");
          sectionRoot.classList.toggle("mm-deck--lecture-freine", deckReadingPart[deckIdx] === "freine");
          // Retour sur la révélation : l'anneau se recompose comme à la découverte.
          // Il a pu être redessiné pendant qu'il était masqué (Safari émet un
          // « resize » quand sa barre d'adresse bouge), donc avec une taille nulle :
          // petit et décalé dans un coin. Le redessiner une fois visible le remet.
          if (deckIdx === 0 && before !== 0 && currentBracelet?.sequence?.length) {
            requestAnimationFrame(() => renderHeroVisual(currentBracelet.sequence, true));
          }
          dots.forEach((d, k) => d.classList.toggle("is-on", k === deckIdx));
          // Au premier écran, « Retour » ramène au formulaire.
          prevBtn.textContent = deckIdx === 0 ? "‹ Modifier" : "‹ Retour";
          nextBtn.hidden = deckIdx >= dernierSuivant;
          surEcranQr = deckIdx === qrIdx;
          // Le minuteur passe à 60 s sur l'écran QR : le toucher qui y mène
          // remonte jusqu'au document APRÈS ce changement (bumpBorneIdle, plus bas).
          if (emportZone && cur === emportZone) {
            preparerEmport();
            prepareQr("lp-emport-qr", "lp-emport-qr-status", false);
          } else if (surEcranQr) prepareQr();
          window.scrollTo(0, 0);
        };
        const next = () => { if (deckIdx < dernierSuivant) goSlide(deckIdx + 1, false); };
        const prev = () => {
          // Avec vendeur, on revient de l'écran QR à l'écran commande d'où l'on venait.
          if (deckIdx === qrIdx && !sansVendeur) goSlide(qrIdx - 2, true);
          else if (deckIdx > 0) goSlide(deckIdx - 1, true);
          else document.getElementById("lp-back-to-form")?.click();
        };
        const versTel = document.getElementById("lp-borne-to-phone");
        if (versTel && qrZone && !sansVendeur) {
          versTel.hidden = false;
          versTel.addEventListener("click", () => goSlide(qrIdx, false));
        }
        nextBtn.addEventListener("click", next);
        prevBtn.addEventListener("click", prev);

        // Glisser du doigt : horizontal franc seulement (un défilement vertical
        // ou un toucher sur un bouton ne doit pas changer d'écran).
        let tx = null, ty = null;
        sectionRoot.addEventListener("touchstart", (e) => {
          if (sectionRoot.dataset.flow !== "result") return;
          tx = e.touches[0].clientX; ty = e.touches[0].clientY;
        }, { passive: true });
        sectionRoot.addEventListener("touchend", (e) => {
          if (tx === null) return;
          const dx = e.changedTouches[0].clientX - tx;
          const dy = e.changedTouches[0].clientY - ty;
          tx = ty = null;
          if (Math.abs(dx) < 70 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          if (dx < 0) next(); else prev();
        }, { passive: true });

        // Chaque nouvelle révélation repart du premier écran.
        new MutationObserver(() => {
          // deckIdx remis à 0 d'abord : la révélation anime déjà l'anneau, il
          // ne faut pas le recomposer une seconde fois par-dessus.
          if (sectionRoot.dataset.flow === "result") {
            for (const k in qrPourSaisie) delete qrPourSaisie[k];
            deckIdx = 0; goSlide(0, false);
            // Nouvelle révélation après un récapitulatif « comptoir » : on rouvre
            // l'écran de commande (taille modifiable, bouton visible).
            sansEmailOk = false;
            if (noEmail && !noEmail.hidden) { noEmail.hidden = true; if (borneForm) borneForm.hidden = false; }
            const recap = document.getElementById("lp-borne-recap");
            if (comptoirMode && recap && !recap.hidden) {
              recap.hidden = true;
              if (borneForm) borneForm.hidden = false;
              if (checkoutHint) checkoutHint.hidden = false;
            }
          }
        }).observe(sectionRoot, { attributes: true, attributeFilter: ["data-flow"] });
        goSlide(0, false);
      }

      /* Retour à l'accueil après inactivité. La borne est un appareil PARTAGÉ :
         sans cela, le prénom et la date de naissance du client précédent restent
         à l'écran pour le suivant.
         ⚠️ 5 min, et non les 90 s de la borne pierres : ici on LIT un portrait
         long, souvent sans toucher l'écran. Un délai court rechargerait la page
         au milieu d'une lecture. */
      const BORNE_IDLE_MS = 300000;
      // Itinérante : une fois le QR affiché, il n'a plus à rester à l'écran.
      const BORNE_IDLE_QR_MS = 60000;
      let borneIdleTimer = null;
      /* Itinérante, réseau coupé : recharger afficherait une page d'erreur
         Safari et bloquerait la borne. On efface alors tout DANS la page et on
         revient au formulaire vierge. */
      const effaceSurPlace = () => {
        sectionRoot.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea')
          .forEach((i) => { i.value = ""; i.disabled = false; });
        sectionRoot.querySelectorAll("#lp-finalize-panel input").forEach((i) => { i.disabled = false; });
        ["lp-borne-qr", "lp-emport-qr"].forEach((id) => {
          const box = document.getElementById(id);
          if (box) box.innerHTML = "";
        });
        for (const k in qrPourSaisie) delete qrPourSaisie[k];
        emportPourSaisie = "";
        ["lp-borne-done", "lp-borne-recap", "lp-borne-noemail"].forEach((id) => {
          const el = document.getElementById(id);
          if (el) el.hidden = true;
        });
        if (borneForm) borneForm.hidden = false;
        if (checkoutHint) checkoutHint.hidden = false;
        if (sendBtn && !comptoirMode) sendBtn.textContent = "Envoyer ma commande en caisse";
        showFormStep();
        window.scrollTo(0, 0);
        bumpBorneIdle();
      };
      const resetBorne = () => {
        // Purge la réhydratation, sinon le rechargement restaurerait le profil
        // qu'on cherche justement à effacer.
        try { sessionStorage.removeItem(LEAD_RESTORE_KEY); } catch (e) { /* indispo : le reload suffit */ }
        // Retour à la veille de la borne (deux messages qui alternent) ; à
        // défaut d'accueil connu, on recharge la page vierge.
        const home = sectionRoot.dataset.borneHome;
        const aller = () => { if (home) window.location.href = home; else window.location.reload(); };
        if (!reglageBorne) { aller(); return; }
        if (navigator.onLine === false) { effaceSurPlace(); return; }
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 4000);
        fetch(home || window.location.href, { method: "HEAD", cache: "no-store", signal: ctrl.signal })
          .then((r) => { clearTimeout(t); if (r.ok) aller(); else effaceSurPlace(); })
          .catch(() => { clearTimeout(t); effaceSurPlace(); });
      };
      function bumpBorneIdle() {
        clearTimeout(borneIdleTimer);
        borneIdleTimer = setTimeout(resetBorne, surEcranQr ? BORNE_IDLE_QR_MS : BORNE_IDLE_MS);
      }
      ["click", "touchstart", "keydown", "input", "scroll"].forEach((ev) => {
        document.addEventListener(ev, bumpBorneIdle, { passive: true });
      });
      bumpBorneIdle();
    }

    firstnamesInput?.addEventListener("input", () => {
      if (!currentBracelet) return;
      currentBracelet.formData.firstnames = firstnamesInput.value.trim() || "";
      currentBracelet.formData.lastname = "";
      renderReading(currentBracelet.official, currentBracelet.profile);
    });

    leadForm?.addEventListener("submit", event => {
      // Soumission NATIVE assumée (le store exige le captcha). On ne
      // preventDefault QUE pour bloquer une saisie invalide. Sur saisie valide :
      // on remplit les tags + persiste l'état, puis on LAISSE hCaptcha + le POST
      // natif se faire (reload). restoreLeadStateIfNeeded() réhydrate au retour.
      const email = emailResultInput?.value.trim() || "";
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        event.preventDefault();
        setLeadMessage("Merci de renseigner une adresse email valide.", "error");
        return;
      }
      const consent = document.getElementById("lp-lead-consent");
      if (consent && !consent.checked) {
        event.preventDefault();
        setLeadMessage("Merci de cocher la case de consentement pour recevoir votre aperçu.", "error");
        return;
      }
      if (!currentBracelet) {
        event.preventDefault();
        setLeadMessage("Générez d'abord votre bracelet pour recevoir votre livret personnalisé.", "error");
        return;
      }

      fillLeadTags();              // contact[tags] = mm-lead + mm-fn:… + mm-dob:…
      persistLeadRestoreState();   // (B) survit au reload → réhydratation au retour
      if (leadSubmitBtn) {
        leadSubmitBtn.disabled = true;
        leadSubmitBtn.textContent = "Envoi en cours…";
      }
      // PAS de preventDefault : notre listener (sur le form) s'exécute avant
      // celui d'hCaptcha (sur document) ; hCaptcha résout puis soumet en natif
      // avec le token → POST + reload ?customer_posted=true.
    });

    // Changement de finition : permute la sprite affichée sans relancer la boucle
    // (silhouette identique → aucune coupure visuelle).
    if (finishSelector) {
      finishSelector.addEventListener("change", (event) => {
        const input = event.target.closest('input[name="mm-finish"]');
        if (!input) return;
        applyInterperleFinish(input.dataset.finish || input.value);
      });
    }

    if (spacerSelector) {
      spacerSelector.addEventListener("change", (event) => {
        const input = event.target.closest('input[name="mm-spacers"]');
        if (!input) return;
        spacersOn = input.value === "avec";
        if (currentBracelet?.sequence?.length) renderHeroVisual(currentBracelet.sequence, true);
      });
    }

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Anneau masqué (écran de borne non affiché) : mesuré à 0, il serait
        // redessiné minuscule. Il sera redessiné en revenant sur son écran.
        if (slotsContainer && !slotsContainer.offsetWidth) return;
        if (currentBracelet?.sequence?.length) renderHeroVisual(currentBracelet.sequence);
      }, 80);
    });

    setStepTracker("form");
    console.log("Mystic Moon — Bracelet d’Alignement (grille officielle JSON) prêt.");
  }

  window.generateBracelet = function deprecatedGenerateBracelet() {
    console.warn("[Mystic Moon] generateBracelet() n’est plus disponible : le moteur d’intentions a été retiré.");
    return null;
  };

  window.MysticMoonEngine = Object.assign({}, window.MysticMoonEngine || {}, {
    parseBirthdate,
    calculateLifePathNumber,
    getWesternZodiacSlug,
    getWesternZodiacFromBirth,
    buildOfficialProfileKey,
    buildProfileFromIdentity,
    loadOfficialProfilesFromAsset,
    getOfficialProfile,
    coerceOfficialProfile,
    resolveComputationFromBirthdate,
    diagnoseOfficialVsShopify,
    ZODIAC_LABEL_FR,
    LIFE_PATH_KEYS
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMysticMoonLifePath);
  } else {
    initMysticMoonLifePath();
  }

  document.addEventListener("shopify:section:load", initMysticMoonLifePath);
  document.addEventListener("shopify:section:reorder", initMysticMoonLifePath);
  document.addEventListener("shopify:section:select", initMysticMoonLifePath);
})();
